'use strict';

(function () {
  const INSTANCE_DESTROY_KEY = '__saCollectionMainDestroy';
  const previousInstanceDestroy = window[INSTANCE_DESTROY_KEY];
  if (typeof previousInstanceDestroy === 'function') {
    try {
      previousInstanceDestroy();
    } catch (e) {
      /* no-op: a stale Theme Editor instance must not block the current section */
    }
  }

  const ROOT_SELECTOR = '.sa-collection-main--product-grid';
  const GRID_CONTAINER_SELECTOR = '#productgridcontainer';
  const GRID_SELECTOR = '#product-grid';
  const CATEGORY_NAV_SELECTOR = '[data-sa-collection-category-nav]';
  const PROMO_RAIL_SELECTOR = '[data-sa-promo-rail]';
  const SORT_SELECTOR = '[data-sa-collection-sort]';
  const FACET_TAB_SELECTOR = '[data-sa-facet-tab]';
  const SORT_OPTION_SELECTOR = '[data-sa-sort-option]';
  const SENTINEL_SELECTOR = '[data-sa-infinite-sentinel]';
  const SKELETON_TEMPLATE_SELECTOR = '[data-sa-skeleton-template]';
  const SCROLL_TOP_RESET_THRESHOLD = 24;
  const SCROLL_COMPACT_THRESHOLD = 120;
  const TEXT_DISCLOSURE_CHARACTER_LIMIT = 60;
  // Minimum scroll movement before we flip Smart Sticky direction. Prevents
  // 1px oscillation from toggling classes back and forth (scroll jitter).
  const SCROLL_DELTA = 12;

  let delegationBound = false;
  let scrollBound = false;
  let lastScrollY = 0;
  let scrollTicking = false;
  let infiniteObserver = null;
  let stickyState = 'top';
  let lastStickyOffset = null;
  let lastHeaderOffset = null;
  let lastStickyLayoutIsMobile = null;
  let addedFocusCleanup = null;
  let addedLastFocused = null;
  let addedLockedCard = null;
  let addedLockedButton = null;
  let promoRepairObserver = null;
  let promoRootRepairObserver = null;
  let promoRepairGrid = null;
  let promoRepairTicking = false;
  let ctaLabelObserver = null;
  let ctaLabelRoot = null;
  let ctaLabelTicking = false;
  let filterTabsResizeBound = false;
  let allProductsCache = null;
  let allProductsCacheKey = '';
  let infiniteScrollHandler = null;
  let initializedRoot = null;
  let initializedGrid = null;
  let dynamicTabsRenderPromise = null;
  let dynamicTabsRenderTarget = null;
  let dynamicTabsRenderStream = null;
  let collectionScrollTransitionInProgress = false;
  let textDisclosureResizeBound = false;
  let cleanupCallbacks = [];
  let timeoutIds = new Set();

  function addTrackedListener(target, type, handler, options) {
    if (!target || !target.addEventListener) return;
    target.addEventListener(type, handler, options);
    cleanupCallbacks.push(() => target.removeEventListener(type, handler, options));
  }

  function trackTimeout(callback, delay) {
    const id = window.setTimeout(() => {
      timeoutIds.delete(id);
      callback();
    }, delay);
    timeoutIds.add(id);
    return id;
  }

  function cleanupCollectionMain() {
    releaseCardAfterAdd();
    addedFocusCleanup = null;
    addedLastFocused = null;
    cleanupCallbacks.forEach((cleanup) => {
      try {
        cleanup();
      } catch (e) {
        /* no-op */
      }
    });
    cleanupCallbacks = [];
    timeoutIds.forEach((id) => window.clearTimeout(id));
    timeoutIds.clear();

    [infiniteObserver, promoRepairObserver, promoRootRepairObserver, ctaLabelObserver].forEach(
      (observer) => {
        if (observer && typeof observer.disconnect === 'function') observer.disconnect();
      }
    );
    infiniteObserver = null;
    promoRepairObserver = null;
    promoRootRepairObserver = null;
    promoRepairGrid = null;
    ctaLabelObserver = null;
    ctaLabelRoot = null;

    document
      .querySelectorAll(
        '[data-sa-indicator-bound],[data-sa-nav-bound],[data-sa-drag-bound],[data-sa-rail-bound],[data-sa-fade-bound]'
      )
      .forEach((el) => {
        delete el.dataset.saIndicatorBound;
        delete el.dataset.saNavBound;
        delete el.dataset.saDragBound;
        delete el.dataset.saRailBound;
        delete el.dataset.saFadeBound;
      });

    delegationBound = false;
    scrollBound = false;
    scrollTicking = false;
    stickyState = 'top';
    lastStickyOffset = null;
    lastHeaderOffset = null;
    lastStickyLayoutIsMobile = null;
    filterTabsResizeBound = false;
    promoRepairTicking = false;
    ctaLabelTicking = false;
    initializedRoot = null;
    initializedGrid = null;
    dynamicTabsRenderPromise = null;
    dynamicTabsRenderTarget = null;
    dynamicTabsRenderStream = null;
    collectionScrollTransitionInProgress = false;
    textDisclosureResizeBound = false;
    document.body.classList.remove('sa-category-nav-dragging', 'sa-filter-tabs-dragging');
    document.documentElement.classList.remove('sa-collection-navigation-transition');
    document.querySelectorAll('[data-sa-category-nav-section].is-hidden').forEach((section) => {
      section.classList.remove('is-hidden');
    });
    document
      .querySelectorAll('[data-sa-category-nav-section].is-sticky-active')
      .forEach((section) => section.classList.remove('is-sticky-active'));
    document.querySelectorAll('.sa-category-nav-wrapper').forEach((wrapper) => {
      wrapper.classList.remove('sa-category-nav-wrapper');
    });
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      root.style.removeProperty('--sa-collection-sticky-top');
    });
  }

  function handleSectionUnload(event) {
    const section = event && event.target;
    if (
      section &&
      section.querySelector &&
      !section.querySelector(
        [
          ROOT_SELECTOR,
          CATEGORY_NAV_SELECTOR,
          PROMO_RAIL_SELECTOR,
          '[data-sa-collection-sort]',
          '[data-sa-empty-state]',
        ].join(',')
      )
    ) {
      return;
    }
    cleanupCollectionMain();
  }

  function handleSectionLoad() {
    init();
    initCategoryNavIndicator();
    initPromoRailIndicator();
    scheduleDynamicTabsRender();
    schedulePromoRepair();
  }

  function destroyCollectionMain() {
    cleanupCollectionMain();
    document.removeEventListener('shopify:section:unload', handleSectionUnload);
    document.removeEventListener('shopify:section:load', handleSectionLoad);
    if (window[INSTANCE_DESTROY_KEY] === destroyCollectionMain) {
      delete window[INSTANCE_DESTROY_KEY];
    }
  }

  function isMobileLayout() {
    return window.matchMedia && window.matchMedia('(max-width: 1024px)').matches;
  }

  function assignV2CollectionPage(url) {
    window.location.assign(url);
  }

  function getHeaderOffset() {
    const stickyHeader = document.querySelector('.shopify-section-header-sticky');
    if (!stickyHeader || stickyHeader.classList.contains('shopify-section-header-hidden')) {
      return 0;
    }
    return stickyHeader.offsetHeight;
  }

  // Theme Editor previews do not always apply the runtime sticky-header class
  // even though the Header is visibly occupying the top of the preview. Use
  // the rendered bottom edge as a visual boundary for the category section;
  // this is intentionally separate from getHeaderOffset(), which controls the
  // actual sticky top offset and must remain zero when the header is not sticky.
  function getHeaderVisualBottom() {
    const headerCandidates = [
      document.querySelector('.shopify-section-header-sticky'),
      document.querySelector('.shopify-section-header'),
      document.querySelector('.section-header'),
      document.querySelector('header.header')
    ];
    const header = headerCandidates.find((candidate) => {
      if (!candidate) return false;
      const rect = candidate.getBoundingClientRect();
      return rect.height > 0 && rect.bottom >= 0;
    });
    if (!header) return 0;
    return Math.max(0, header.getBoundingClientRect().bottom);
  }

  // The theme keeps product images at opacity:0.01 (.bls-image-js .bls-loading-image)
  // and only reveals them via a `load` listener. Already-cached / already-complete
  // images (common in preview, repeat views, and AJAX-appended cards) never fire
  // `load` again, so they stay invisible. Reveal them within v2 cards only: drop the
  // class for complete images, and self-heal the rest with a one-shot listener.
  function revealCardImages(scope) {
    const root = scope && scope.querySelectorAll ? scope : document;
    const imgs = root.querySelectorAll('.sa-collection-product-card img.bls-loading-image');
    imgs.forEach((img) => {
      const clear = () => {
        img.classList.remove('bls-loading-image');
        const motion = img.closest('motion-element');
        if (motion) motion.classList.remove('bls-loading-image');
      };
      if (img.complete && img.naturalWidth > 0) {
        clear();
      } else if (!img.dataset.saReveal) {
        img.dataset.saReveal = '1';
        const done = () => {
          clear();
          img.removeEventListener('load', done);
          img.removeEventListener('error', done);
        };
        img.addEventListener('load', done);
        img.addEventListener('error', done);
      }
    });
  }

  function reinitMedia() {
    if (typeof initLazyloadItem === 'function') {
      try {
        initLazyloadItem();
      } catch (e) {
        /* no-op */
      }
    }
    if (window.BlsLazyloadImg && typeof window.BlsLazyloadImg.init === 'function') {
      try {
        window.BlsLazyloadImg.init();
      } catch (e) {
        /* no-op */
      }
    }
    revealCardImages();
    initCardColorStates();
    repairCollectionCtaLabels();
    initTextDisclosures();
  }

  function getDirectTextNodeValue(el) {
    if (!el) return '';
    const node = Array.prototype.find.call(
      el.childNodes,
      (child) => child.nodeType === Node.TEXT_NODE && child.textContent.trim()
    );
    return node ? node.textContent : '';
  }

  function setDirectTextNodeValue(el, value) {
    if (!el) return;
    const node = Array.prototype.find.call(
      el.childNodes,
      (child) => child.nodeType === Node.TEXT_NODE && child.textContent.trim()
    );
    if (node) {
      if (value) {
        if (node.textContent !== value) node.textContent = value;
      } else {
        node.remove();
      }
    } else {
      if (value) el.insertBefore(document.createTextNode(value), el.firstChild);
    }
  }

  function updateTextDisclosureContainer(disclosure) {
    const overlay = disclosure.closest('.sa-collection-product-card__overlay');
    const hoverMedia = disclosure.closest('.sa-collection-product-card__hover-media');
    if (overlay) {
      overlay.classList.toggle(
        'has-expanded-disclosure',
        !!overlay.querySelector('[data-sa-text-disclosure].is-expanded')
      );
    }
    if (hoverMedia) {
      hoverMedia.classList.toggle(
        'has-expanded-disclosure',
        !!hoverMedia.querySelector('[data-sa-text-disclosure].is-expanded')
      );
    }
  }

  function getTextDisclosureFullText(content) {
    if (!content.dataset.saTextDisclosureFull) {
      content.dataset.saTextDisclosureFull = content.textContent || '';
    }
    return content.dataset.saTextDisclosureFull;
  }

  function getTextDisclosureMaxHeight(content) {
    const styles = window.getComputedStyle(content);
    return Number.parseFloat(styles.lineHeight) * 2 || Number.parseFloat(styles.fontSize) * 2.6;
  }

  function textDisclosureFitsTwoLines(disclosure, content, trigger) {
    const disclosureBounds = disclosure.getBoundingClientRect();
    const triggerBounds = trigger.getBoundingClientRect();
    const triggerBottom = triggerBounds.bottom - disclosureBounds.top;
    const renderedHeight = Math.max(disclosure.scrollHeight, triggerBottom);

    return renderedHeight <= getTextDisclosureMaxHeight(content) + 1;
  }

  function fitTextDisclosureToTwoLines(disclosure, content, fullText) {
    const trigger = disclosure.querySelector('[data-sa-text-disclosure-trigger]');
    if (!trigger) return;

    trigger.hidden = false;
    const characters = Array.from(fullText);
    let start = 0;
    let end = Math.min(characters.length, TEXT_DISCLOSURE_CHARACTER_LIMIT);
    let best = 0;

    while (start <= end) {
      const middle = Math.floor((start + end) / 2);
      content.textContent = characters.slice(0, middle).join('').trimEnd();

      if (textDisclosureFitsTwoLines(disclosure, content, trigger)) {
        best = middle;
        start = middle + 1;
      } else {
        end = middle - 1;
      }
    }

    content.textContent = characters.slice(0, best).join('').trimEnd();
  }

  function measureTextDisclosure(disclosure) {
    const content = disclosure.querySelector('[data-sa-text-disclosure-content]');
    const trigger = disclosure.querySelector('[data-sa-text-disclosure-trigger]');
    if (!content || !trigger) return;

    const fullText = getTextDisclosureFullText(content);
    const wasExpanded = disclosure.classList.contains('is-expanded');
    disclosure.classList.remove('is-collapsed');
    disclosure.classList.remove('is-expanded');
    trigger.hidden = true;
    content.textContent = fullText;

    const overflowsTwoLines = content.scrollHeight > getTextDisclosureMaxHeight(content) + 1;
    const exceedsCharacterLimit =
      Array.from(fullText).length > TEXT_DISCLOSURE_CHARACTER_LIMIT;

    if (!overflowsTwoLines && !exceedsCharacterLimit) {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-label', trigger.getAttribute('data-expand-label') || '');
      updateTextDisclosureContainer(disclosure);
      return;
    }

    trigger.hidden = false;
    if (wasExpanded) {
      disclosure.classList.add('is-expanded');
      content.textContent = fullText;
      trigger.setAttribute('aria-expanded', 'true');
      trigger.setAttribute('aria-label', trigger.getAttribute('data-collapse-label') || '');
      updateTextDisclosureContainer(disclosure);
      return;
    }

    disclosure.classList.add('is-collapsed');
    fitTextDisclosureToTwoLines(disclosure, content, fullText);
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-label', trigger.getAttribute('data-expand-label') || '');
    updateTextDisclosureContainer(disclosure);
  }

  function initTextDisclosures() {
    const disclosures = document.querySelectorAll('[data-sa-text-disclosure]');
    if (!disclosures.length) return;

    window.requestAnimationFrame(() => {
      document.querySelectorAll('[data-sa-text-disclosure]').forEach(measureTextDisclosure);
    });

    if (textDisclosureResizeBound) return;
    textDisclosureResizeBound = true;
    addTrackedListener(
      window,
      'resize',
      () => {
        window.requestAnimationFrame(() => {
          document.querySelectorAll('[data-sa-text-disclosure]').forEach(measureTextDisclosure);
        });
      },
      { passive: true }
    );
  }

  function repairCollectionCtaLabels(scope) {
    const root = scope && scope.querySelectorAll ? scope : document;
    root.querySelectorAll('.sa-collection-product-card [data-sa-collection-cta-label]').forEach((cta) => {
      const label = cta.getAttribute('data-sa-collection-cta-label') || '';
      if (!label) return;
      const card = cta.closest('.sa-collection-product-card');
      const form = cta.closest('product-form');
      const isUnavailableColor = card && card.classList.contains('sa-color-soldout');
      const isUnavailableProduct = form && form.getAttribute('data-available') === 'false';
      const isSoldOutAddButton =
        cta.hasAttribute('data-sold-out-message') &&
        (cta.hasAttribute('disabled') || cta.getAttribute('aria-disabled') === 'true') &&
        (isUnavailableProduct || isUnavailableColor);
      if (isSoldOutAddButton) return;
      const labelNode =
        cta.querySelector('.btn-action') ||
        cta.querySelector('.hidden-on-load') ||
        cta.querySelector('span:not(.sold-out-message):not(.icon-load)') ||
        null;

      if (labelNode) {
        labelNode.classList.remove('hidden');
        labelNode.removeAttribute('aria-hidden');
        labelNode.style.removeProperty('display');
        if (labelNode.textContent !== label) labelNode.textContent = label;
      }

      const directText = getDirectTextNodeValue(cta);
      if (directText.trim() || /translation missing/i.test(cta.textContent || '')) {
        setDirectTextNodeValue(cta, labelNode ? '' : label);
      }

      if (
        cta.tagName !== 'A' &&
        (cta.hasAttribute('disabled') || cta.getAttribute('aria-disabled') === 'true')
      ) {
        cta.setAttribute('aria-disabled', 'true');
      }

      if (/translation missing/i.test(cta.getAttribute('aria-label') || '') || !cta.getAttribute('aria-label')) {
        cta.setAttribute('aria-label', label);
      }
    });
  }

  function scheduleCtaLabelRepair() {
    if (ctaLabelTicking) return;
    ctaLabelTicking = true;
    window.requestAnimationFrame(() => {
      ctaLabelTicking = false;
      repairCollectionCtaLabels();
    });
  }

  function initCtaLabelObserver() {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root || !('MutationObserver' in window)) return;
    if (ctaLabelObserver && ctaLabelRoot === root) return;
    if (ctaLabelObserver) ctaLabelObserver.disconnect();
    ctaLabelRoot = root;
    ctaLabelObserver = new MutationObserver(scheduleCtaLabelRepair);
    ctaLabelObserver.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'aria-label']
    });
  }

  /* ---------- Sticky offset + mobile smart sticky (AJAX-replace safe) ---------- */

  function getCategoryNavSection(categoryNav) {
    return categoryNav && categoryNav.closest
      ? categoryNav.closest('[data-sa-category-nav-section]')
      : null;
  }

  // Shopify wraps every section in a same-height `.shopify-section` element.
  // A sticky element inside that wrapper can only remain sticky until the
  // wrapper's 200px-ish height is exhausted. Flatten the wrapper for the
  // category image navigation so its sticky containing block is the page,
  // allowing it to remain visible while the product grid scrolls.
  function syncCategoryNavWrapper(categoryNav) {
    const categoryNavSection = getCategoryNavSection(categoryNav);
    const wrapper = categoryNavSection && categoryNavSection.parentElement;
    if (wrapper && wrapper.classList.contains('shopify-section')) {
      wrapper.classList.add('sa-category-nav-wrapper');
    }
  }

  function syncCategoryNavStickySpacing(categoryNav, headerOffset) {
    const categoryNavSection = getCategoryNavSection(
      categoryNav || document.querySelector(CATEGORY_NAV_SELECTOR)
    );
    if (!categoryNavSection) return;
    const stickyBoundary = Math.max(headerOffset, getHeaderVisualBottom());
    const collapseSpacing =
      isMobileLayout() &&
      categoryNavSection.getBoundingClientRect().top <= stickyBoundary + 1;
    categoryNavSection.classList.toggle('is-sticky-active', collapseSpacing);
  }

  function applyStickyOffset(categoryNav) {
    const headerOffset = getHeaderOffset();
    const nextLayoutIsMobile = isMobileLayout();
    const categoryNavSection = getCategoryNavSection(
      categoryNav || document.querySelector(CATEGORY_NAV_SELECTOR)
    );
    syncCategoryNavStickySpacing(categoryNav, headerOffset);
    const categoryNavVisible =
      nextLayoutIsMobile &&
      categoryNavSection &&
      !categoryNavSection.classList.contains('is-hidden');
    const categoryNavOffset = categoryNavVisible ? categoryNavSection.offsetHeight : 0;
    const nextOffset = headerOffset + categoryNavOffset;
    if (
      nextOffset === lastStickyOffset &&
      headerOffset === lastHeaderOffset &&
      nextLayoutIsMobile === lastStickyLayoutIsMobile
    ) {
      return;
    }
    lastStickyOffset = nextOffset;
    lastHeaderOffset = headerOffset;
    lastStickyLayoutIsMobile = nextLayoutIsMobile;
    document.documentElement.style.setProperty('--sa-collection-category-nav-top', headerOffset + 'px');
    document.documentElement.style.setProperty('--sa-collection-sticky-top', nextOffset + 'px');
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      root.style.setProperty('--sa-collection-sticky-top', nextOffset + 'px');
    });
  }

  function applyStickyState(root, categoryNav, nextState) {
    const categoryNavSection = getCategoryNavSection(categoryNav);
    // A wheel/touch scroll upward decreases scrollY and restores the full
    // navigation. A wheel/touch scroll downward increases scrollY and keeps
    // only the secondary filter/sort bar visible.
    const shouldHideCategoryNav = nextState === 'down';
    if (categoryNavSection) {
      categoryNavSection.classList.toggle('is-hidden', shouldHideCategoryNav);
    }
    const stateChanged = nextState !== stickyState;
    stickyState = nextState;
    if (stateChanged) {
      root.classList.toggle('is-scrolling-down', nextState === 'down');
      root.classList.toggle('is-scrolling-up', nextState === 'up');
      root.classList.toggle('is-compact-sticky', nextState === 'down');
    }
    applyStickyOffset(categoryNav);
    // The theme header updates its hidden/sticky class in its own scroll handler.
    // Recalculate on the following frame so this bar reserves only a visible
    // header, regardless of listener registration order.
    window.requestAnimationFrame(() => applyStickyOffset(categoryNav));
  }

  function onSmartStickyScroll() {
    if (scrollTicking) return;
    scrollTicking = true;
    window.requestAnimationFrame(() => {
      const root = document.querySelector(ROOT_SELECTOR);
      const categoryNav = document.querySelector(CATEGORY_NAV_SELECTOR);
      const isDesktop = window.matchMedia('(min-width: 1025px)').matches;

      // Shopify Theme Editor can scroll an inner preview document/container
      // without updating window.scrollY. Keep the sticky spacing in sync from
      // the section's actual viewport position on every scroll tick.
      syncCategoryNavStickySpacing(categoryNav, getHeaderOffset());

      if (collectionScrollTransitionInProgress) {
        scrollTicking = false;
        return;
      }

      if (!root || isDesktop) {
        // Desktop (or no grid): clear any lingering mobile sticky state.
        if (root && stickyState !== 'top') applyStickyState(root, categoryNav, 'top');
        scrollTicking = false;
        return;
      }

      const currentY = window.scrollY;
      const delta = currentY - lastScrollY;

      if (currentY <= SCROLL_TOP_RESET_THRESHOLD) {
        applyStickyState(root, categoryNav, 'top');
        lastScrollY = currentY;
      } else if (Math.abs(delta) > SCROLL_DELTA) {
        if (delta > 0 && currentY < SCROLL_COMPACT_THRESHOLD) {
          lastScrollY = currentY;
          scrollTicking = false;
          return;
        }
        // Only react past the threshold, and only re-baseline on real moves.
        applyStickyState(root, categoryNav, delta > 0 ? 'down' : 'up');
        lastScrollY = currentY;
      }
      scrollTicking = false;
    });
  }

  function initSticky() {
    const categoryNav = document.querySelector(CATEGORY_NAV_SELECTOR);
    const categoryNavSection = getCategoryNavSection(categoryNav);
    syncCategoryNavWrapper(categoryNav);
    if (categoryNavSection) categoryNavSection.classList.remove('is-hidden');
    applyStickyOffset(categoryNav);
    if (scrollBound) return;
    scrollBound = true;
    lastScrollY = window.scrollY;
    addTrackedListener(
      window,
      'resize',
      () => {
        const layoutChanged = isMobileLayout() !== lastStickyLayoutIsMobile;
        if (layoutChanged || !isMobileLayout() || window.scrollY <= SCROLL_TOP_RESET_THRESHOLD) {
          applyStickyOffset();
        }
        onSmartStickyScroll();
        // PC↔mobile switches can re-run the theme lazyload against already-complete
        // images (which never re-fire `load`); re-reveal so images stay visible.
        revealCardImages();
      },
      { passive: true }
    );
    addTrackedListener(window, 'scroll', onSmartStickyScroll, { passive: true });
    // Capture scroll events from the Theme Editor preview's nested scrolling
    // container as well as the top-level window.
    addTrackedListener(document, 'scroll', onSmartStickyScroll, {
      passive: true,
      capture: true
    });
  }

  /* ---------- Loading state ---------- */

  function setLoading(state) {
    const root = document.querySelector(ROOT_SELECTOR);
    if (root) root.classList.toggle('is-loading', !!state);
  }

  /* ---------- Seamless batch loading (IntersectionObserver) ---------- */

  function appendStreamNode(s, node) {
    if (!s || !s.grid || !node) return;
    if (s.sentinel && s.sentinel.parentNode === s.grid) {
      s.grid.insertBefore(node, s.sentinel);
      return;
    }
    s.grid.appendChild(node);
  }

  function appendSkeletons(s, count) {
    const template = document.querySelector(SKELETON_TEMPLATE_SELECTOR);
    const nodes = [];
    if (!s || !s.grid || !template || !template.content) return nodes;
    for (let i = 0; i < count; i += 1) {
      const clone = template.content.cloneNode(true);
      const card = clone.firstElementChild;
      if (!card) continue;
      // The skeleton snippet ships hidden; reveal it for the loading state.
      card.removeAttribute('hidden');
      card.setAttribute('data-sa-skeleton-loading', 'true');
      appendStreamNode(s, card);
      nodes.push(card);
    }
    return nodes;
  }

  function removeNodes(nodes) {
    nodes.forEach((node) => node.remove());
  }

  // Visual-stream state. Batching is by VISUAL cards (product cards + promo
  // cards), not by product page. Promos are spliced at their grid_position so a
  // promo consumes one of the N cards in a batch instead of adding an extra one.
  let stream = null;

  function hasPromoAt(s, idx) {
    if (s.promosActive === false) return false;
    return Object.prototype.hasOwnProperty.call(s.promoByPos, idx);
  }

  function getNextPendingPromoPosition(s) {
    if (!s || s.promosActive === false) return null;
    const next = Object.keys(s.promoByPos)
      .map((position) => Number(position))
      .filter((position) => position > s.composedVisual)
      .sort((a, b) => a - b)[0];
    return Number.isFinite(next) ? next : null;
  }

  function appendPromo(s, idx) {
    const html = s.promoByPos[idx];
    if (!html) return;
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const node = tmp.firstElementChild;
    if (node) {
      node.classList.remove('sa-collection-promotion-card--rail');
      node.removeAttribute('data-sa-rendered-promo-card');
      appendStreamNode(s, node);
    }
  }

  function normalizePromoNode(node) {
    if (!node) return null;
    node.classList.remove('sa-collection-promotion-card--rail');
    node.removeAttribute('data-sa-rendered-promo-card');
    return node;
  }

  function collectPromoEntries() {
    const byPos = {};
    const add = (pos, html) => {
      const numericPos = parseInt(pos, 10);
      if (!(numericPos > 0) || !html || Object.prototype.hasOwnProperty.call(byPos, numericPos)) {
        return;
      }
      byPos[numericPos] = html;
    };

    document.querySelectorAll('[data-sa-promo-card]').forEach((tpl) => {
      const pos = tpl.getAttribute('data-grid-position');
      if (!tpl.content) return;
      const node = tpl.content.firstElementChild;
      if (node) add(pos, node.outerHTML);
    });

    document
      .querySelectorAll('[data-sa-promo-rail] .sa-collection-promotion-card--rail')
      .forEach((node) => {
        add(node.getAttribute('data-grid-position'), node.outerHTML);
      });

    document
      .querySelectorAll('#product-grid > [data-sa-rendered-promo-card], #product-grid > .sa-collection-promotion-card:not(.sa-collection-promotion-card--rail)')
      .forEach((node) => {
        add(node.getAttribute('data-grid-position'), node.outerHTML);
      });

    return Object.keys(byPos)
      .map((pos) => ({ pos: parseInt(pos, 10), html: byPos[pos] }))
      .sort((a, b) => a.pos - b.pos);
  }

  function repairDesktopPromosFromDom() {
    if (isMobileLayout() || activeTabKey || activeSeriesKey) return false;
    const grid = document.querySelector(GRID_SELECTOR);
    if (!grid) return false;
    if (stream && stream.grid === grid && stream.promosActive) return false;
    const existing = grid.querySelector(
      ':scope > .sa-collection-promotion-card:not(.sa-collection-promotion-card--rail)'
    );
    if (existing) return false;

    const entries = collectPromoEntries();
    if (!entries.length) return false;

    entries.forEach((entry) => {
      const tmp = document.createElement('div');
      tmp.innerHTML = entry.html;
      const node = normalizePromoNode(tmp.firstElementChild);
      if (!node) return;
      const target = grid.children[Math.max(0, entry.pos - 1)] || null;
      grid.insertBefore(node, target);
    });
    reinitMedia();
    revealCardImages(grid);
    return true;
  }

  function schedulePromoRepair() {
    if (promoRepairTicking) return;
    promoRepairTicking = true;
    window.requestAnimationFrame(() => {
      promoRepairTicking = false;
      initPromoRepairObserver();
      repairDesktopPromosFromDom();
    });
  }

  // Product slots (non-promo positions) within the next `count` visual cells.
  function countProductSlots(s, startIdx, count) {
    let n = 0;
    for (let i = 0; i < count; i += 1) {
      if (!hasPromoAt(s, startIdx + i)) n += 1;
    }
    return n;
  }

  function buildStreamNextPageUrl(s, page) {
    const container = document.querySelector(GRID_CONTAINER_SELECTOR);
    const sourceHref =
      (s && s.sourceUrl) ||
      (container && container.getAttribute('data-sa-grid-source-url')) ||
      window.location.href;
    const target = new URL(sourceHref, window.location.href);
    const current = new URL(window.location.href);

    // Keep the current collection context (sort/filter/theme preview) while
    // changing only the pagination page and the source collection path.
    target.search = current.search;
    target.searchParams.set('page', String(page));
    if (!target.searchParams.has('view')) {
      target.searchParams.set('view', 'v2-refactor');
    }
    return target.toString();
  }

  function stopPaginationStream(s) {
    if (!s) return;
    s.nextUrl = null;
    s.paginationFailed = true;
  }

  async function fetchNextProducts(s) {
    if (!s.nextUrl) return false;
    try {
      const requestUrl = buildV2UrlFromCurrentContext(s.nextUrl);
      const requestPage = Number(requestUrl.searchParams.get('page')) || s.currentPage + 1;
      const res = await fetch(requestUrl.toString(), {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' }
      });
      if (!res.ok) throw new Error('Request failed: ' + res.status);
      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const newGrid = doc.querySelector(GRID_SELECTOR);
      const newCards = newGrid
        ? Array.from(newGrid.querySelectorAll(':scope > .sa-collection-product-card'))
        : [];
      // A default-template response has no V2 cards. Stop this stream so its
      // sentinel cannot keep requesting the same invalid page.
      if (!newCards.length) {
        stopPaginationStream(s);
        return false;
      }
      newCards.forEach((node) => s.productQueue.push(document.importNode(node, true)));

      const newSentinel = doc.querySelector(SENTINEL_SELECTOR);
      s.currentPage = requestPage;
      const serverNextUrl = newSentinel && newSentinel.getAttribute('data-next-url');
      s.nextUrl =
        serverNextUrl ||
        (s.totalProducts > s.currentPage * s.serverPageSize
          ? buildStreamNextPageUrl(s, s.currentPage + 1)
          : null);
      return true;
    } catch (e) {
      stopPaginationStream(s);
      return false;
    }
  }

  function finalizeSentinel(s) {
    const hasMore =
      s.productQueue.length > 0 || !!s.nextUrl || getNextPendingPromoPosition(s) !== null;
    if (!hasMore) {
      s.done = true;
      if (infiniteObserver) infiniteObserver.disconnect();
      if (s.sentinel && s.sentinel.parentNode) s.sentinel.remove();
    }
  }

  async function composeBatch(s) {
    if (!s || s.loading || s.done) return;
    s.loading = true;
    let placed = 0;
    while (placed < s.batch) {
      const nextIdx = s.composedVisual + 1;
      if (hasPromoAt(s, nextIdx)) {
        appendPromo(s, nextIdx);
        s.composedVisual += 1;
        placed += 1;
        continue;
      }
      if (s.productQueue.length > 0) {
        appendStreamNode(s, s.productQueue.shift());
        s.composedVisual += 1;
        placed += 1;
        continue;
      }
      // Queue empty mid-batch: fetch the next product page to finish this batch.
      if (s.nextUrl) {
        const need = countProductSlots(s, nextIdx, s.batch - placed);
        const skeletons = appendSkeletons(s, need);
        const ok = await fetchNextProducts(s);
        removeNodes(skeletons);
        if (!ok || s.productQueue.length === 0) {
          s.done = s.nextUrl ? s.done : true;
          break;
        }
        continue;
      }
      // No more products anywhere.
      const nextPromoPosition = getNextPendingPromoPosition(s);
      if (nextPromoPosition !== null) {
        // A merchant can place a promo after the final product position. Move
        // the visual cursor to that configured slot so the promo is still
        // rendered instead of ending the stream at the last product card.
        s.composedVisual = nextPromoPosition - 1;
        continue;
      }
      s.done = true;
      break;
    }
    reinitMedia();
    initCtaLabelObserver();
    finalizeSentinel(s);
    // [DTC-840] 无限滚动批次追加的卡片错过 DOMContentLoaded 一次性精修，对网格重新精修（真实 IP 结果有缓存，不重复请求）
    if (typeof refineRegionByRealIP === 'function' && s.grid) refineRegionByRealIP(s.grid);
    s.loading = false;

    // Re-arm: if the sentinel is still visible and more remains, keep loading.
    if (
      infiniteObserver &&
      !s.done &&
      s.sentinel &&
      s.sentinel.isConnected &&
      isInViewport(s.sentinel)
    ) {
      infiniteObserver.unobserve(s.sentinel);
      infiniteObserver.observe(s.sentinel);
    }
  }

  function isInViewport(el) {
    const rect = el.getBoundingClientRect();
    return rect.top < window.innerHeight + 400 && rect.bottom > -400;
  }

  function buildStream() {
    const grid = document.querySelector(GRID_SELECTOR);
    if (!grid) return null;
    const sentinel = document.querySelector(SENTINEL_SELECTOR);
    const cachedProducts = getCachedAllProducts();
    const gridContainer = document.querySelector(GRID_CONTAINER_SELECTOR);
    // The first paint is always one configured visual batch. When Liquid has no
    // paginate.next (for collections up to 50 products), create a client-only
    // sentinel so the remaining server-rendered cards still arrive in batches.
    const configuredBatch = parseInt(
      (gridContainer || grid).getAttribute('data-sa-batch-size'),
      10
    );
    const serverPageSize =
      parseInt((gridContainer || grid).getAttribute('data-sa-server-page-size'), 10) ||
      parseInt(grid.getAttribute('data-sa-server-page-size'), 10) ||
      configuredBatch ||
      9;
    const sentinelBatch = sentinel ? parseInt(sentinel.getAttribute('data-batch'), 10) : 0;
    const batch = configuredBatch || sentinelBatch || 9;
    const totalProducts = parseInt(grid.getAttribute('data-sa-total-products'), 10) || 0;
    const currentPage = parseInt(grid.getAttribute('data-sa-page'), 10) || 1;
    const sourceUrl = gridContainer && gridContainer.getAttribute('data-sa-grid-source-url');
    const hasUnrenderedProducts = totalProducts > currentPage * serverPageSize;

    const promoByPos = {};
    collectPromoEntries().forEach((entry) => {
      let targetPos = entry.pos;
      while (Object.prototype.hasOwnProperty.call(promoByPos, targetPos)) {
        targetPos += 1;
      }
      promoByPos[targetPos] = entry.html;
    });

    // Detach server-rendered visual cards before rebuilding the stream. Liquid
    // renders promo cards for first-paint/Theme Editor reliability; JS removes
    // them here so the composed stream never duplicates them.
    grid
      .querySelectorAll(':scope > .sa-collection-promotion-card:not(.sa-collection-promotion-card--rail)')
      .forEach((node) => node.remove());

    // Detach the server-rendered product cards into a queue; they are re-placed
    // in visual order (with promos interleaved) in batches of `batch`.
    const productQueue = [];
    grid.querySelectorAll(':scope > .sa-collection-product-card').forEach((node) => {
      productQueue.push(node);
    });
    productQueue.forEach((node) => node.remove());

    let streamSentinel = sentinel;
    const promoCount = Object.keys(promoByPos).length;
    const hasUnrenderedPromos =
      !cachedProducts &&
      Object.keys(promoByPos).some((position) => Number(position) > batch);
    const hasUnrenderedVisuals = !cachedProducts && productQueue.length + promoCount > batch;
    if (
      !cachedProducts &&
      !streamSentinel &&
      (hasUnrenderedProducts || hasUnrenderedVisuals || hasUnrenderedPromos)
    ) {
      streamSentinel = document.createElement('div');
      streamSentinel.className = 'sa-collection-main__infinite-sentinel';
      streamSentinel.setAttribute('data-sa-infinite-sentinel', '');
      streamSentinel.setAttribute('data-batch', String(batch));
      streamSentinel.setAttribute('aria-hidden', 'true');
      if (hasUnrenderedProducts) {
        streamSentinel.setAttribute(
          'data-next-url',
          buildStreamNextPageUrl({ sourceUrl: sourceUrl }, currentPage + 1)
        );
      }
      grid.appendChild(streamSentinel);
    }

    if (
      !cachedProducts &&
      streamSentinel &&
      !streamSentinel.getAttribute('data-next-url') &&
      hasUnrenderedProducts
    ) {
      streamSentinel.setAttribute(
        'data-next-url',
        buildStreamNextPageUrl({ sourceUrl: sourceUrl }, currentPage + 1)
      );
    }

    return {
      grid: grid,
      sentinel: streamSentinel,
      batch: batch,
      promoByPos: promoByPos,
      productQueue: cachedProducts ? [] : productQueue,
      composedVisual: 0,
      nextUrl: cachedProducts ? null : streamSentinel ? streamSentinel.getAttribute('data-next-url') : null,
      totalProducts: totalProducts,
      currentPage: currentPage,
      serverPageSize: serverPageSize,
      sourceUrl: sourceUrl,
      loading: false,
      paginationFailed: false,
      done: !!cachedProducts,
      promosActive: !isMobileLayout(),
      filterKey: null,
      allProducts: cachedProducts,
      allProductsLoadPromise: null
    };
  }

  function observeInfiniteStream(s) {
    if (infiniteObserver) {
      infiniteObserver.disconnect();
      infiniteObserver = null;
    }
    if (!s || !s.sentinel || s.done) return;

    if ('IntersectionObserver' in window) {
      infiniteObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) composeBatch(s);
          });
        },
        { rootMargin: '400px 0px' }
      );
      if (s.sentinel.isConnected) infiniteObserver.observe(s.sentinel);
    }
  }

  function initInfinite() {
    if (infiniteObserver) {
      infiniteObserver.disconnect();
      infiniteObserver = null;
    }
    if (infiniteScrollHandler) {
      window.removeEventListener('scroll', infiniteScrollHandler);
      window.removeEventListener('resize', infiniteScrollHandler);
      infiniteScrollHandler = null;
    }
    stream = buildStream();
    if (!stream) return;
    initializedRoot = document.querySelector(ROOT_SELECTOR);
    initializedGrid = stream.grid;

    if (stream.allProducts) {
      if (stream.sentinel && stream.sentinel.parentNode) {
        stream.sentinel.remove();
        stream.sentinel = null;
      }
      const urlTabKey = getTabKeyFromUrl();
      const products = stream.allProducts.filter((node) => {
        const tabOk = !urlTabKey || cardTabKeys(node).indexOf(urlTabKey) !== -1;
        const seriesOk = !activeSeriesKey || cardCollections(node).indexOf(activeSeriesKey) !== -1;
        return tabOk && seriesOk;
      });
      composeAllVisual(stream, products, !urlTabKey && !activeSeriesKey && !isMobileLayout());
      updateCount(products.length + getVisiblePromoCount(stream));
      toggleEmptyState(products.length === 0);
      reinitMedia();
      return;
    }

    // Compose the first batch immediately (promo + products = `batch` cards).
    composeBatch(stream);

    if (!stream.sentinel || stream.done) return;
    observeInfiniteStream(stream);

    // Theme Editor previews can scroll an iframe/container without consistently
    // notifying IntersectionObserver. Keep the observer for normal storefronts
    // and use the same guarded batch loader as a scroll fallback.
    infiniteScrollHandler = () => {
      if (!stream || stream.done || stream.loading || !stream.sentinel?.isConnected) return;
      if (isInViewport(stream.sentinel)) composeBatch(stream);
    };
    addTrackedListener(window, 'scroll', infiniteScrollHandler, { passive: true });
    addTrackedListener(window, 'resize', infiniteScrollHandler, { passive: true });
    infiniteScrollHandler();
  }

  function initPromoRepairObserver() {
    const grid = document.querySelector(GRID_SELECTOR);
    if (promoRepairObserver && promoRepairGrid === grid) return;
    if (promoRepairObserver) {
      promoRepairObserver.disconnect();
      promoRepairObserver = null;
    }
    promoRepairGrid = grid;
    if (!grid || !('MutationObserver' in window)) return;
    promoRepairObserver = new MutationObserver(() => {
      schedulePromoRepair();
    });
    promoRepairObserver.observe(grid, { childList: true });
  }

  function initPromoRootRepairObserver() {
    if (promoRootRepairObserver || !('MutationObserver' in window)) return;
    const root = document.querySelector(ROOT_SELECTOR) || document.body;
    if (!root) return;
    promoRootRepairObserver = new MutationObserver(() => {
      initPromoRepairObserver();
      schedulePromoRepair();
    });
    promoRootRepairObserver.observe(root, { childList: true, subtree: true });
  }

  /* ---------- Client-side pill tabs (no S&D facet configured) ----------
     Tabs are generated in Liquid from products' custom.tab_category (see grid
     section). Since the grid is paginated (server renders one batch, the rest
     stream in on scroll), filtering client-side first drains every remaining page
     so counts stay correct across the whole collection, then re-renders only the
     matching cards. "All" restores the full product+promo sequence. */

  function cardTabKeys(node) {
    const raw = node.getAttribute('data-product-tab-keys') || '';
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }

  function cardTabEntries(node) {
    const raw = node.getAttribute('data-product-tab-entries') || '';
    return raw
      .split('|||')
      .map((entry) => {
        const parts = entry.split('::');
        const key = (parts[0] || '').trim();
        const label = (parts[1] || parts[0] || '').trim();
        return key && label ? { key: key, label: label } : null;
      })
      .filter(Boolean);
  }

  function cardCollections(node) {
    const raw = node.getAttribute('data-product-collections') || '';
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }

  function getTabParam() {
    const tabs = document.querySelector('[data-sa-dynamic-tabs]');
    return tabs ? tabs.getAttribute('data-tab-param') || '' : '';
  }

  function handleize(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/['"]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function getTabKeyFromUrl(url) {
    const tabParam = getTabParam();
    if (!tabParam) return '';
    const source = url || window.location.href;
    try {
      const value = new URL(source, window.location.href).searchParams.get(tabParam);
      return handleize(value);
    } catch (e) {
      return '';
    }
  }

  function syncTabUrlParam(key) {
    const tabParam = getTabParam();
    if (!tabParam || !window.history || !window.history.pushState) return;
    const url = new URL(window.location.href);
    if (key) {
      const active = Array.prototype.find.call(
        document.querySelectorAll('[data-sa-cs-tab]'),
        (tab) => (tab.getAttribute('data-cs-key') || '') === key
      );
      const value = active
        ? active.getAttribute('data-cs-value') || active.textContent.trim()
        : key;
      url.searchParams.set(tabParam, value);
    } else {
      url.searchParams.delete(tabParam);
    }
    window.history.pushState({ saClientTab: true }, '', url.toString());
  }

  function getAllProductsCacheKey(url) {
    const tabParam = getTabParam();
    try {
      const target = new URL(url || window.location.href, window.location.href);
      if (tabParam) target.searchParams.delete(tabParam);
      target.searchParams.delete('page');
      return target.pathname + '?' + target.searchParams.toString();
    } catch (e) {
      return '';
    }
  }

  function getCachedAllProducts() {
    const key = getAllProductsCacheKey();
    if (!key || key !== allProductsCacheKey || !allProductsCache || !allProductsCache.length) {
      return null;
    }
    return allProductsCache;
  }

  function setCachedAllProducts(products) {
    const key = getAllProductsCacheKey();
    if (!key || !products || !products.length) return;
    allProductsCacheKey = key;
    allProductsCache = products;
  }

  function updateCount(n) {
    const el = document.querySelector('[data-sa-collection-count]');
    if (!el) return;
    const singular = el.getAttribute('data-count-singular') || '';
    const plural = (el.getAttribute('data-count-plural') || singular).replace(/^items$/i, 'item(s)');
    const label = n <= 1 ? singular : plural;
    el.textContent = label ? n + ' ' + label : String(n);
  }

  function getVisiblePromoCount(s) {
    const promoRail = document.querySelector(PROMO_RAIL_SELECTOR);
    if (isMobileLayout()) {
      return promoRail
        ? promoRail.querySelectorAll('.sa-collection-promotion-card--rail').length
        : 0;
    }
    if (!s || !s.promosActive || !s.promoByPos) return 0;
    return Object.keys(s.promoByPos).length;
  }

  function normalizeCollectionCountLabels(scope) {
    const root = scope && scope.querySelectorAll ? scope : document;
    root.querySelectorAll('[data-sa-collection-count]').forEach((el) => {
      const match = (el.textContent || '').trim().match(/^(\d+)\s+item(?:s|\(s\))?$/i);
      if (!match) return;
      const count = Number(match[1]);
      updateCount(Number.isNaN(count) ? 0 : count);
    });
  }

  // Toggle the client-side empty state when a pill tab / series filter yields no
  // products. The empty element lives outside #product-grid, so it survives the
  // grid re-render in composeAllVisual.
  function toggleEmptyState(isEmpty) {
    const emptyEl = document.querySelector('[data-sa-empty-state]');
    if (emptyEl) emptyEl.hidden = !isEmpty;
  }

  function wait(ms) {
    return new Promise((resolve) => trackTimeout(resolve, ms));
  }

  async function ensureAllProductsLoaded(sourceStream) {
    const targetStream = sourceStream || stream;
    if (!targetStream) return [];
    if (targetStream.allProducts) return targetStream.allProducts;
    if (targetStream.allProductsLoadPromise) return targetStream.allProductsLoadPromise;

    targetStream.allProductsLoadPromise = (async () => {
      while (targetStream.loading && targetStream === stream) {
        await wait(50);
      }
      if (targetStream !== stream) return [];
      if (!targetStream.allProducts) {
        targetStream.loading = true;
        try {
          let guard = 0;
          while (targetStream === stream && targetStream.nextUrl && guard < 50) {
            const ok = await fetchNextProducts(targetStream);
            guard += 1;
            if (!ok) break;
          }
          const composed = Array.prototype.slice.call(
            targetStream.grid.querySelectorAll(':scope > .sa-collection-product-card')
          );
          const allProducts = composed.concat(targetStream.productQueue);
          const loadComplete =
            targetStream === stream &&
            !targetStream.nextUrl &&
            !targetStream.paginationFailed;
          if (loadComplete) {
            targetStream.allProducts = allProducts;
            setCachedAllProducts(targetStream.allProducts);
            targetStream.nextUrl = null;
          }
          // Dynamic tab discovery may preload later pages. Keep those cards in
          // the stream queue and keep the sentinel alive so the viewport still
          // controls when the preloaded cards become visible.
          targetStream.done =
            targetStream.productQueue.length === 0 &&
            !targetStream.nextUrl &&
            getNextPendingPromoPosition(targetStream) === null;
          if (!loadComplete) return allProducts;
        } finally {
          targetStream.loading = false;
        }
      }
      return targetStream === stream ? targetStream.allProducts || [] : [];
    })();

    try {
      return await targetStream.allProductsLoadPromise;
    } finally {
      targetStream.allProductsLoadPromise = null;
    }
  }

  function getTabConfigMap() {
    const config = new Map();
    document.querySelectorAll('[data-sa-tab-config]').forEach((tpl, index) => {
      const key = tpl.getAttribute('data-tab-key') || '';
      if (!key || config.has(key)) return;
      config.set(key, {
        label: tpl.getAttribute('data-tab-label') || '',
        hide: tpl.getAttribute('data-tab-hide') === 'true',
        order: index
      });
    });
    return config;
  }

  function setDynamicTabsLoading(tabs, isLoading) {
    if (!tabs) return;
    tabs.classList.toggle('is-loading-tabs', !!isLoading);
    tabs.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    if (!isLoading) {
      tabs.querySelectorAll('[data-sa-tab-placeholder]').forEach((node) => {
        node.remove();
      });
    }
  }

  function getRenderedProductCards(sourceStream) {
    const targetStream = sourceStream || stream;
    if (!targetStream || !targetStream.grid) return [];
    return Array.prototype.slice.call(
      targetStream.grid.querySelectorAll(':scope > .sa-collection-product-card')
    );
  }

  function getOrderedDynamicTabs(products) {
    const config = getTabConfigMap();
    const byKey = new Map();

    products.forEach((product) => {
      cardTabEntries(product).forEach((entry) => {
        if (!byKey.has(entry.key)) byKey.set(entry.key, entry.label);
      });
    });

    return Array.from(byKey.entries())
      .map(([key, label]) => {
        const itemConfig = config.get(key);
        return {
          key: key,
          value: label,
          label: itemConfig && itemConfig.label ? itemConfig.label : label,
          hide: itemConfig ? itemConfig.hide : false,
          order: itemConfig ? itemConfig.order : 9999,
          configured: !!itemConfig
        };
      })
      .filter((item) => !item.hide)
      .sort((a, b) => {
        if (a.order !== b.order) return a.order - b.order;
        if (a.configured !== b.configured) return a.configured ? -1 : 1;
        return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
      });
  }

  function syncDynamicTabsMarkup(tabs, ordered) {
    const nextTabs = ordered.map((item) => [item.key, item.value, item.label]);
    const currentTabs = Array.from(
      tabs.querySelectorAll('[data-sa-cs-tab]:not([data-cs-key=""])')
    ).map((node) => [
      node.getAttribute('data-cs-key') || '',
      node.getAttribute('data-cs-value') || '',
      node.textContent || ''
    ]);

    if (JSON.stringify(currentTabs) === JSON.stringify(nextTabs)) return;

    tabs.querySelectorAll('[data-sa-cs-tab]:not([data-cs-key=""])').forEach((node) => {
      node.remove();
    });

    ordered.forEach((item) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'sa-collection-main__filter-tab';
      button.setAttribute('data-sa-cs-tab', '');
      button.setAttribute('data-cs-key', item.key);
      button.setAttribute('data-cs-value', item.value);
      button.textContent = item.label;
      tabs.appendChild(button);
    });
  }

  function syncDynamicTabSelection(tabs, ordered) {
    activeTabKey = getTabKeyFromUrl();
    const availableKeys = ordered
      ? ordered.map((item) => item.key)
      : Array.from(tabs.querySelectorAll('[data-sa-cs-tab]')).map(
          (node) => node.getAttribute('data-cs-key') || ''
        );
    const currentExists = !activeTabKey || availableKeys.indexOf(activeTabKey) !== -1;
    if (!currentExists) activeTabKey = '';
    setActiveCsTab(activeTabKey);
  }

  async function renderDynamicTabs() {
    const tabs = document.querySelector('[data-sa-dynamic-tabs]');
    const targetStream = stream;
    if (!tabs || !targetStream) return;
    if (
      dynamicTabsRenderPromise &&
      dynamicTabsRenderTarget === tabs &&
      dynamicTabsRenderStream === targetStream
    ) {
      return dynamicTabsRenderPromise;
    }

    const renderPromise = (async () => {
      setDynamicTabsLoading(tabs, true);
      try {
        const hasServerTabs = tabs.hasAttribute('data-sa-server-tabs');
        if (hasServerTabs) {
          syncDynamicTabSelection(tabs);
          setDynamicTabsLoading(tabs, false);
          // Search & Discovery already supplied the complete available tab set.
          // Do not drain every pagination page just to rediscover the same tabs.
          return;
        } else {
          const initialProducts = getRenderedProductCards(targetStream).concat(
            targetStream.productQueue
          );
          const initialTabs = getOrderedDynamicTabs(initialProducts);
          if (initialTabs.length > 0) {
            syncDynamicTabsMarkup(tabs, initialTabs);
            syncDynamicTabSelection(tabs, initialTabs);
            setDynamicTabsLoading(tabs, false);
          }
        }

        const products = await ensureAllProductsLoaded(targetStream);
        if (!tabs.isConnected || stream !== targetStream) return;
        const ordered = getOrderedDynamicTabs(products);
        // Keep the first-paint tabs until every page has loaded. A failed
        // background request must not turn a temporary empty result into a
        // destructive navigation reset that leaves only the All tab.
        if (targetStream.allProducts) {
          syncDynamicTabsMarkup(tabs, ordered);
          syncDynamicTabSelection(tabs, ordered);
        }
        if (activeTabKey) await applyCombinedFilter();
      } finally {
        setDynamicTabsLoading(tabs, false);
        if (tabs.isConnected && stream === targetStream) initFilterTabsFade();
      }
    })();

    dynamicTabsRenderPromise = renderPromise;
    dynamicTabsRenderTarget = tabs;
    dynamicTabsRenderStream = targetStream;

    try {
      return await renderPromise;
    } finally {
      if (dynamicTabsRenderPromise === renderPromise) {
        dynamicTabsRenderPromise = null;
        dynamicTabsRenderTarget = null;
        dynamicTabsRenderStream = null;
      }
    }
  }

  function setActiveCsTab(key) {
    document.querySelectorAll('[data-sa-cs-tab]').forEach((btn) => {
      const isActive = (btn.getAttribute('data-cs-key') || '') === (key || '');
      btn.classList.toggle('is-active', isActive);
      if (isActive) {
        btn.setAttribute('aria-current', 'true');
        scrollFilterTabIntoView(btn);
      } else {
        btn.removeAttribute('aria-current');
      }
    });
    window.requestAnimationFrame(updateFilterTabsFade);
  }

  function setActiveSeriesTab(key) {
    document.querySelectorAll('[data-sa-series-filter]').forEach((el) => {
      const isActive = (el.getAttribute('data-series-key') || '') === (key || '');
      el.classList.toggle('is-active', isActive);
      if (isActive) {
        el.setAttribute('aria-current', 'page');
      } else {
        el.removeAttribute('aria-current');
      }
    });
  }

  function setBreadcrumbCurrentTitle(title) {
    const current = document.querySelector('.sa-collection-main__breadcrumb-current');
    if (!current) return;
    if (title && title.trim()) current.textContent = title.trim();
  }

  function getCategoryNavTitle(item) {
    if (!item) return '';
    return (
      item.getAttribute('data-category-title') ||
      item.getAttribute('data-series-title') ||
      ((item.querySelector('.sa-collection-main__category-label') || item).textContent || '')
    ).trim();
  }

  function updateBreadcrumbTitleFromActiveCategoryNav() {
    const active = document.querySelector(
      '[data-sa-collection-category-nav] .sa-collection-main__category-item.is-active'
    );
    setBreadcrumbCurrentTitle(getCategoryNavTitle(active));
  }

  function updateBreadcrumbTitleFromSeries(key) {
    const active = document.querySelector(
      '[data-sa-series-filter][data-series-key="' +
        (window.CSS && CSS.escape ? CSS.escape(key || '') : key || '') +
        '"]'
    );
    if (active) {
      setBreadcrumbCurrentTitle(getCategoryNavTitle(active));
      return;
    }
    updateBreadcrumbTitleFromActiveCategoryNav();
  }

  // Lay out the full visual sequence (products, with promos interleaved at their
  // grid_position only when unfiltered) into the grid in one pass — every card is
  // already loaded at this point, so no sentinel/observer is needed.
  function composeAllVisual(s, products, withPromos) {
    s.grid.innerHTML = '';
    s.promosActive = withPromos;
    let visual = 0;
    let pi = 0;
    while (pi < products.length) {
      visual += 1;
      if (withPromos && Object.prototype.hasOwnProperty.call(s.promoByPos, visual)) {
        appendPromo(s, visual);
        continue;
      }
      s.grid.appendChild(products[pi]);
      pi += 1;
    }
    if (withPromos) {
      // Trailing promos positioned right after the last product (e.g. a promo at
      // the final visual slot) still get placed.
      let next = visual + 1;
      while (Object.prototype.hasOwnProperty.call(s.promoByPos, next)) {
        appendPromo(s, next);
        next += 1;
      }
    }
  }

  async function composeClientFilterStream(s, products, withPromos) {
    if (!s || !s.grid) return;

    if (s.sentinel && s.sentinel.parentNode) s.sentinel.remove();
    s.sentinel = null;
    if (infiniteObserver) {
      infiniteObserver.disconnect();
      infiniteObserver = null;
    }

    s.grid.innerHTML = '';
    s.promosActive = withPromos;
    s.productQueue = products.slice();
    s.composedVisual = 0;
    s.nextUrl = null;
    s.totalProducts = products.length;
    s.currentPage = 1;
    s.done = false;

    const promoCount = withPromos ? Object.keys(s.promoByPos).length : 0;
    if (products.length + promoCount > s.batch) {
      const sentinel = document.createElement('div');
      sentinel.className = 'sa-collection-main__infinite-sentinel';
      sentinel.setAttribute('data-sa-infinite-sentinel', '');
      sentinel.setAttribute('data-batch', String(s.batch));
      sentinel.setAttribute('aria-hidden', 'true');
      s.sentinel = sentinel;
      s.grid.appendChild(sentinel);
    }

    const firstBatch = composeBatch(s);
    observeInfiniteStream(s);
    await firstBatch;
  }

  // Two independent client-side filter dimensions that combine (AND):
  //   activeTabKey    — product custom.tab_category (the pill tabs / "type")
  //   activeSeriesKey — collection membership (the big image nav / "series")
  let activeTabKey = '';
  let activeSeriesKey = '';

  async function applyCombinedFilter() {
    if (!stream) return;
    setLoading(true);
    try {
      const allProducts = await ensureAllProductsLoaded();
      if (!stream.allProducts) return;

      const noFilter = !activeTabKey && !activeSeriesKey;
      const filtered = allProducts.filter((n) => {
        const tabOk = !activeTabKey || cardTabKeys(n).indexOf(activeTabKey) !== -1;
        const seriesOk = !activeSeriesKey || cardCollections(n).indexOf(activeSeriesKey) !== -1;
        return tabOk && seriesOk;
      });

      // Promos only interleave in the unfiltered ("All / All") view. Filtered
      // results use the same invisible 9+9 stream as the initial collection.
      await composeClientFilterStream(stream, filtered, noFilter && !isMobileLayout());
      updateCount(filtered.length + getVisiblePromoCount(stream));
      toggleEmptyState(filtered.length === 0);
    } finally {
      setLoading(false);
    }
  }

  function syncFilterActiveStates() {
    setActiveCsTab(activeTabKey);
    setActiveSeriesTab(activeSeriesKey);
    updateBreadcrumbTitleFromSeries(activeSeriesKey);
  }

  function applyClientTabFilter(key) {
    activeTabKey = key || '';
    syncFilterActiveStates();
    return applyCombinedFilter();
  }

  function isMobileFilterBarSticky() {
    if (!isMobileLayout()) return false;
    const sticky = document.querySelector('[data-sa-collection-sticky]');
    if (!sticky) return false;
    return sticky.getBoundingClientRect().top <= getHeaderOffset() + 1;
  }

  async function navigateClientTabFilter(key) {
    syncTabUrlParam(key || '');
    const shouldResetMobileScroll = isMobileFilterBarSticky();
    if (shouldResetMobileScroll) beginCollectionScrollTransition();
    try {
      await applyClientTabFilter(key);
      if (shouldResetMobileScroll) await settleMobileFilterResult();
    } finally {
      if (shouldResetMobileScroll) endCollectionScrollTransition();
    }
  }

  function applyClientSeriesFilter(key) {
    activeSeriesKey = key || '';
    syncFilterActiveStates();
    return applyCombinedFilter();
  }

  function closeCollectionQuickview() {
    const modal = document.querySelector('.tingle-modal--visible.tingle-modal.quickview');
    if (!modal) return;
    const closeButton = modal.querySelector('.tingle-modal__close');
    if (closeButton) {
      closeButton.click();
      return;
    }
    modal.remove();
    document.body.classList.remove('tingle-enabled');
    document.documentElement.classList.remove('open-popup');
    document.documentElement.style.removeProperty('padding-right');
  }

  async function restoreClientFiltersFromHistory() {
    if (!document.querySelector(ROOT_SELECTOR)) return;
    closeCollectionQuickview();
    if (!stream) initInfinite();
    if (!stream) return;
    activeTabKey = getTabKeyFromUrl();
    await renderDynamicTabs();
    syncFilterActiveStates();
    await applyCombinedFilter();
    initFilterTabsFade();
    initPromoRailIndicator();
    schedulePromoRepair();
    scheduleCtaLabelRepair();
  }

  function scheduleHistoryRestore(event) {
    const navEntry =
      window.performance &&
      performance.getEntriesByType &&
      performance.getEntriesByType('navigation')[0];
    const isHistoryRestore = !!(event && event.persisted) || (navEntry && navEntry.type === 'back_forward');
    if (!isHistoryRestore) return;
    window.requestAnimationFrame(() => {
      closeCollectionQuickview();
      restoreClientFiltersFromHistory();
    });
  }

  function buildAllProductsUrl(fallbackHref) {
    const url = buildV2UrlFromCurrentContext(fallbackHref || window.location.href);
    const tabParam = getTabParam();
    if (tabParam) url.searchParams.delete(tabParam);
    url.searchParams.delete('sort_by');
    Array.from(url.searchParams.keys()).forEach((key) => {
      if (key.indexOf('filter.') === 0 || key === 'page') url.searchParams.delete(key);
    });
    return url;
  }

  function resetClientCollectionFilters(fallbackHref) {
    const url = buildAllProductsUrl(fallbackHref);
    if (url.pathname !== window.location.pathname) {
      assignV2CollectionPage(url.toString());
      return Promise.resolve();
    }
    if (!stream) {
      assignV2CollectionPage(url.toString());
      return Promise.resolve();
    }
    activeTabKey = '';
    activeSeriesKey = '';
    window.history.pushState({ saCategory: true }, '', url.toString());
    syncFilterActiveStates();
    return applyCombinedFilter().then(scrollToGridTop);
  }

  /* ---------- Out-of-stock color state (scoped to v2 cards) ----------
     The theme's <variant-radios> already swaps the image + price on swatch click.
     It does NOT gray the CTA for the item card, and the PRD wants the price to read
     the sold-out state when an out-of-stock color is selected. We only reflect availability:
     toggle a card-level class so CSS can gray the CTA + swap the price state,
     and disable the real add button so it can't be submitted. */

  function setCardColorSoldOut(card, soldOut) {
    if (!card) return;
    card.classList.toggle('sa-color-soldout', soldOut);
    // Only restore when the product itself isn't server-disabled (fully sold out).
    const form = card.querySelector('product-form');
    const serverAvailable = !form || form.getAttribute('data-available') !== 'false';
    // Gray + block add/select CTAs so an out-of-stock color can't be submitted.
    // Checkout keeps its own label/action; it is not the unavailable add-button state.
    const ctas = card.querySelectorAll(
      '.product__add-cart, .product-item__select-options, .product-item__button-submit, button[name="add"]'
    );
    ctas.forEach((cta) => {
      // [DTC-840] Go to Local Site 是导航按钮（复用宿主 CTA 样式类才会被本选择器命中），与库存无关，售罄色不得禁用
      if (cta.classList.contains('js-region-buy-btn')) return;
      if (soldOut) {
        cta.setAttribute('aria-disabled', 'true');
        if (cta.tagName === 'BUTTON') cta.disabled = true;
      } else if (serverAvailable) {
        cta.removeAttribute('aria-disabled');
        if (cta.tagName === 'BUTTON') cta.disabled = false;
      }
    });
  }

  function getCardSelectedVariant(card) {
    if (!card) return null;
    const swatches = card.querySelector('variant-radios');
    const active = swatches && swatches.querySelector('.product__color-swatches--js.active');
    const variantsScript = swatches && swatches.querySelector('script[type="application/json"]');
    if (!active || !variantsScript) return null;

    let variants;
    try {
      variants = JSON.parse(variantsScript.textContent || '[]');
    } catch (e) {
      return null;
    }
    if (!Array.isArray(variants)) return null;

    const position = Number(active.getAttribute('data-position') || 1);
    const value = active.getAttribute('data-value');
    return variants.find((variant) => {
      if (!variant || !value) return false;
      if (position === 1) return variant.option1 === value;
      if (position === 2) return variant.option2 === value;
      if (position === 3) return variant.option3 === value;
      return false;
    });
  }

  function syncCardSelectedVariant(card) {
    const variant = getCardSelectedVariant(card);
    if (!variant || !variant.id) return;

    card.querySelectorAll('product-form form [name="id"]').forEach((input) => {
      input.value = variant.id;
      input.disabled = false;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    card.querySelectorAll('[data-sa-checkout]').forEach((button) => {
      button.setAttribute('data-variant-id', variant.id);
    });
  }

  function formatCardMoney(value) {
    const cents = Number(value);
    if (!Number.isFinite(cents)) return '';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      const format =
        (window.themeGlobalVariables &&
          window.themeGlobalVariables.settings &&
          window.themeGlobalVariables.settings.money_format) ||
        (window.cartStrings && window.cartStrings.money_format);
      return window.Shopify.formatMoney(cents, format);
    }
    return '';
  }

  function rememberCardComparePrice(card) {
    if (!card || card.dataset.saCompareRemembered === '1') return;
    const compareEl = card.querySelector('.card-product-price .compare-price');
    if (!compareEl || !compareEl.textContent.trim()) return;
    card.dataset.saCompareRemembered = '1';
    card.dataset.saCompareText = compareEl.innerHTML;
    card.dataset.saCompareClass = compareEl.className || 'price-item compare-price dark-grey';
    const compareWrap = compareEl.closest('.price-regular, .price-sale');
    card.dataset.saCompareWrapClass = compareWrap ? compareWrap.className : 'price-regular';
  }

  function syncCardComparePrice(card) {
    if (!card) return;
    rememberCardComparePrice(card);
    const variant = getCardSelectedVariant(card);
    const priceWrap = card.querySelector('.card-product-price');
    if (!variant || !priceWrap) return;

    const comparePrice = Number(variant.compare_at_price || 0);
    const price = Number(variant.price || 0);
    let compareEl = priceWrap.querySelector('.compare-price');
    const fallbackCompareText = card.dataset.saCompareText || '';

    if (comparePrice > price || fallbackCompareText) {
      if (!compareEl) {
        const compareWrap = document.createElement('div');
        compareWrap.className = card.dataset.saCompareWrapClass || 'price-regular';
        compareEl = document.createElement('s');
        compareEl.className = card.dataset.saCompareClass || 'price-item compare-price dark-grey';
        compareWrap.appendChild(compareEl);
        priceWrap.appendChild(compareWrap);
      }
      const formattedCompare = formatCardMoney(comparePrice);
      if (comparePrice > price && formattedCompare) {
        compareEl.innerHTML = formattedCompare;
      } else if (fallbackCompareText) {
        compareEl.innerHTML = fallbackCompareText;
      }
      priceWrap.querySelector('.price-regular')?.classList.add('primary-color');
      priceWrap.querySelector('.price-regular .price')?.classList.add('price--special');
      return;
    }

    compareEl?.closest('.price-regular, .price-sale')?.remove();
    priceWrap.querySelector('.price-regular')?.classList.remove('primary-color');
    priceWrap.querySelector('.price-regular .price')?.classList.remove('price--special');
  }

  function applyCardColorAvailability(card, swatch) {
    if (!card || !swatch) return;
    syncCardSelectedVariant(card);
    const available = swatch.getAttribute('data-available') !== 'false';
    setCardColorSoldOut(card, !available);
    repairCollectionCtaLabels(card);
    trackTimeout(() => repairCollectionCtaLabels(card), 120);
    requestAnimationFrame(() => syncCardComparePrice(card));
    trackTimeout(() => syncCardComparePrice(card), 80);
  }

  // Reflect the initially-selected color on load / after AJAX re-render. The
  // default active swatch is the first in-stock color, so this is usually a no-op;
  // it exists so a re-rendered grid never keeps a stale sold-out state.
  function initCardColorStates() {
    document.querySelectorAll('.sa-collection-product-card').forEach((card) => {
      rememberCardComparePrice(card);
      const active = card.querySelector('.product__color-swatches--js.active');
      if (active) applyCardColorAvailability(card, active);
    });
  }

  /* ---------- v2-scoped AJAX (facet tabs + sort), no .filter-blocks dependency ---------- */

  function scrollToGridTop() {
    const container = document.querySelector(GRID_CONTAINER_SELECTOR);
    if (!container) return;
    const top = container.getBoundingClientRect().top + window.pageYOffset - 100;
    window.scrollTo({ top: top, behavior: 'smooth' });
  }

  function scrollToMobileFilterResult(behavior) {
    const emptyState = document.querySelector('[data-sa-empty-state]:not([hidden])');
    const target = emptyState || (stream && stream.grid) || document.querySelector(GRID_SELECTOR);
    if (!target) return;

    const sticky = document.querySelector('[data-sa-collection-sticky]');
    const stickyHeight = sticky ? sticky.getBoundingClientRect().height : 0;
    const top = Math.max(
      0,
      target.getBoundingClientRect().top +
        window.pageYOffset -
        getHeaderOffset() -
        stickyHeight
    );
    window.scrollTo({ top: top, behavior: behavior || 'smooth' });
    lastScrollY = top;
  }

  function scrollToCollectionTop() {
    const target =
      document.querySelector('.section-sa-collection-main') ||
      document.querySelector('.section-sa-collection-category-nav') ||
      document.querySelector(ROOT_SELECTOR) ||
      document.querySelector(GRID_CONTAINER_SELECTOR);
    if (!target) return;
    const top = Math.max(0, target.getBoundingClientRect().top + window.pageYOffset - getHeaderOffset());
    window.scrollTo({ top: top, behavior: 'auto' });
    lastScrollY = top;
  }

  function nextAnimationFrame() {
    return new Promise((resolve) => window.requestAnimationFrame(resolve));
  }

  function beginCollectionScrollTransition() {
    collectionScrollTransitionInProgress = true;
    document.documentElement.classList.add('sa-collection-navigation-transition');
  }

  function endCollectionScrollTransition() {
    collectionScrollTransitionInProgress = false;
    document.documentElement.classList.remove('sa-collection-navigation-transition');
    onSmartStickyScroll();
  }

  async function settleMobileFilterResult() {
    await nextAnimationFrame();
    await nextAnimationFrame();
    const root = document.querySelector(ROOT_SELECTOR);
    const categoryNav = document.querySelector(CATEGORY_NAV_SELECTOR);
    if (root) applyStickyState(root, categoryNav, 'top');
    scrollToMobileFilterResult('auto');
  }

  async function settleCategoryNavigationScroll() {
    await nextAnimationFrame();
    await nextAnimationFrame();
    const root = document.querySelector(ROOT_SELECTOR);
    const categoryNav = document.querySelector(CATEGORY_NAV_SELECTOR);
    if (root) applyStickyState(root, categoryNav, 'top');
    scrollToCollectionTop();
  }

  async function navigateFacet(url) {
    const container = document.querySelector(GRID_CONTAINER_SELECTOR);
    if (!container) {
      assignV2CollectionPage(url);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(buildGridFetchUrl(url).toString());
      if (!res.ok) throw new Error('Request failed: ' + res.status);
      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const newContainer = doc.querySelector(GRID_CONTAINER_SELECTOR);
      // #productgridcontainer also exists in the default collection template, so
      // require a v2-only marker before swapping. If the fetch fell back to the
      // default collection (missing ?view=), do a full navigation instead of
      // replacing the DOM with the default Availability/Price/Category toolbar.
      const isV2 = doc.querySelector(ROOT_SELECTOR);
      if (!newContainer || !isV2) {
        assignV2CollectionPage(url);
        return;
      }
      // Full container swap resets tab active state, count, batch and sentinel.
      container.innerHTML = newContainer.innerHTML;
      syncGridSourceUrl(container, newContainer);
      normalizeCollectionCountLabels(container);
      // [DTC-840] 筛选重渲染的卡片重新精修 region 购买态
      if (typeof refineRegionByRealIP === 'function') refineRegionByRealIP(container);
      window.history.pushState({ saFacet: true }, '', url);
      reinitMedia();
      initCtaLabelObserver();
      initInfinite();
      initPromoRepairObserver();
      initPromoRailIndicator();
      // Tabs are re-rendered inside the swapped container; re-bind the fade.
      const dynamicTabsPromise = renderDynamicTabs();
      if (activeTabKey || activeSeriesKey) {
        await dynamicTabsPromise;
      }
      initFilterTabsFade();
      // Sort is a server round-trip that rebuilds the grid from the FULL
      // collection. Client-side filters (pill tabs in cs mode / big image series
      // nav) live only in JS state, not the URL, so the fresh grid would snap
      // back to "All". Re-apply the persisted client-side filter so sorting keeps
      // the current tab/series. (In S&D facet mode activeTabKey is empty and the
      // facet is already in the URL, so this is a no-op there.)
      if (activeTabKey || activeSeriesKey) {
        setActiveCsTab(activeTabKey);
        setActiveSeriesTab(activeSeriesKey);
        await applyCombinedFilter();
      }
      schedulePromoRepair();
      scrollToGridTop();
    } catch (e) {
      assignV2CollectionPage(url);
    } finally {
      setLoading(false);
    }
  }

  function buildV2UrlFromCurrentContext(href) {
    const target = new URL(href, window.location.href);
    const current = new URL(window.location.href);
    const keepParams = ['view', 'preview_theme_id', '_fd', 'pb'];
    keepParams.forEach((name) => {
      if (current.searchParams.has(name) && !target.searchParams.has(name)) {
        target.searchParams.set(name, current.searchParams.get(name));
      }
    });
    if (!target.searchParams.has('view')) {
      target.searchParams.set('view', 'v2-refactor');
    }
    return target;
  }

  // A visible All alias can use a different collection as its grid source.
  // Keep the browser URL unchanged, but fetch sort/facet results from that same
  // source collection so a refresh cannot replace the populated grid with an
  // empty alias response.
  function buildGridFetchUrl(href) {
    const target = buildV2UrlFromCurrentContext(href);
    const container = document.querySelector(GRID_CONTAINER_SELECTOR);
    const sourceHref = container && container.getAttribute('data-sa-grid-source-url');
    if (!sourceHref) return target;

    try {
      const source = new URL(sourceHref, window.location.href);
      if (source.origin !== target.origin || source.pathname === target.pathname) {
        return target;
      }
      source.search = target.search;
      return source;
    } catch (e) {
      return target;
    }
  }

  function syncGridSourceUrl(container, sourceContainer) {
    if (!container || !sourceContainer) return;
    const sourceHref = sourceContainer.getAttribute('data-sa-grid-source-url');
    if (sourceHref) {
      container.setAttribute('data-sa-grid-source-url', sourceHref);
    } else {
      container.removeAttribute('data-sa-grid-source-url');
    }
  }

  function buildProductDetailUrlFromCurrentContext(href) {
    const target = new URL(href, window.location.href);
    const current = new URL(window.location.href);
    const keepParams = ['preview_theme_id', '_fd', 'pb'];
    keepParams.forEach((name) => {
      if (current.searchParams.has(name) && !target.searchParams.has(name)) {
        target.searchParams.set(name, current.searchParams.get(name));
      }
    });
    target.searchParams.delete('view');
    target.searchParams.delete('section_id');
    target.searchParams.delete('ajax');
    return target;
  }

  function normalizeProductDetailNavigation(event) {
    const link = event.target.closest(
      '.sa-collection-product-card a[href*="/products/"]'
    );
    if (!link || !link.closest(ROOT_SELECTOR)) return false;
    if (link.target && link.target !== '_self') return false;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return false;

    const href = link.getAttribute('href');
    if (!href || href.indexOf('/products/') === -1) return false;

    let target;
    try {
      target = buildProductDetailUrlFromCurrentContext(href);
    } catch (e) {
      return false;
    }

    if (target.origin !== window.location.origin || target.pathname.indexOf('/products/') === -1) {
      return false;
    }

    event.preventDefault();
    window.location.assign(target.toString());
    return true;
  }

  async function navigateCategory(href, updateHistory = true) {
    const target = buildV2UrlFromCurrentContext(href);
    const currentUrl = new URL(window.location.href);
    // Carry over an explicit shopper choice; otherwise use the destination
    // collection's default sort without adding a URL parameter.
    const requestedSort = currentUrl.searchParams.get('sort_by');
    if (!target.searchParams.has('sort_by') && requestedSort) {
      target.searchParams.set('sort_by', requestedSort);
    }
    const sameOrigin = target.origin === window.location.origin;
    const isCollectionPath = /\/collections\/[^/?#]+/.test(target.pathname);
    if (!sameOrigin || !isCollectionPath) {
      window.location.assign(target.toString());
      return;
    }

    const breadcrumbSection = document.querySelector('.section-sa-collection-main');
    const categorySection = document.querySelector('.section-sa-collection-category-nav');
    const gridContainer = document.querySelector(GRID_CONTAINER_SELECTOR);
    if (!breadcrumbSection || !categorySection || !gridContainer) {
      assignV2CollectionPage(target.toString());
      return;
    }

    setLoading(true);
    beginCollectionScrollTransition();
    try {
      const res = await fetch(target.toString());
      if (!res.ok) throw new Error('Request failed: ' + res.status);
      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const nextBreadcrumb = doc.querySelector('.section-sa-collection-main');
      const nextCategory = doc.querySelector('.section-sa-collection-category-nav');
      const nextGrid = doc.querySelector(GRID_CONTAINER_SELECTOR);
      const isV2 = doc.querySelector(ROOT_SELECTOR);
      if (!nextBreadcrumb || !nextCategory || !nextGrid || !isV2) {
        assignV2CollectionPage(target.toString());
        return;
      }

      breadcrumbSection.innerHTML = nextBreadcrumb.innerHTML;
      categorySection.innerHTML = nextCategory.innerHTML;
      gridContainer.innerHTML = nextGrid.innerHTML;
      syncGridSourceUrl(gridContainer, nextGrid);
      normalizeCollectionCountLabels(gridContainer);
      // [DTC-840] 分类切换重渲染的卡片重新精修 region 购买态
      if (typeof refineRegionByRealIP === 'function') refineRegionByRealIP(gridContainer);
      activeTabKey = '';
      activeSeriesKey = '';
      if (updateHistory) {
        window.history.pushState({ saCategory: true }, '', target.toString());
      }
      reinitMedia();
      initInfinite();
      initPromoRepairObserver();
      initCategoryNavIndicator();
      initPromoRailIndicator();
      updateBreadcrumbTitleFromActiveCategoryNav();
      // The first-pass cards already provide enough data for client-side tabs.
      // Let the complete tab reconciliation continue in the background so a
      // category navigation is not held open by every pagination request.
      renderDynamicTabs();
      initFilterTabsFade();
      schedulePromoRepair();
      await settleCategoryNavigationScroll();
    } catch (e) {
      assignV2CollectionPage(target.toString());
    } finally {
      endCollectionScrollTransition();
      setLoading(false);
    }
  }

  /* ---------- Sort dropdown ---------- */

  function closeAllSortDropdowns(except) {
    document.querySelectorAll(SORT_SELECTOR + '.is-open').forEach((sortRoot) => {
      if (sortRoot === except) return;
      sortRoot.classList.remove('is-open');
      const trigger = sortRoot.querySelector('[data-sa-sort-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
  }

  function closeSortSheet(sheet) {
    if (!sheet) return;
    sheet.classList.remove('is-open');
    sheet.setAttribute('aria-hidden', 'true');
    const sortRoot = sheet.closest(SORT_SELECTOR);
    const trigger = sortRoot ? sortRoot.querySelector('[data-sa-sort-trigger]') : null;
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  function openSortSheet(sheet, trigger) {
    if (!sheet) return;
    sheet.classList.add('is-open');
    sheet.setAttribute('aria-hidden', 'false');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    const panel = sheet.querySelector('.sa-collection-main__sort-sheet-panel');
    if (panel) panel.focus();
  }

  /* ---------- Add to cart → scoped confirmation modal (Figma 136:80) ----------
     Collection cards render the theme's <product-form>, whose global custom
     element would open the site minicart on success. Per PRD, the v2 collection
     shows its own centered confirmation modal instead. We intercept the card
     form submit in the CAPTURE phase and stopImmediatePropagation so the global
     product-form handler never runs, then do a scoped Ajax add and open the
     section-level modal. The global theme.js is left untouched (blast radius
     stays inside the v2 collection). Only the direct single-variant add form is
     intercepted; Select Options (quickview) is unaffected. */

  function getAddedModal(card) {
    var root = card ? card.closest(ROOT_SELECTOR) : document.querySelector(ROOT_SELECTOR);
    if (!root) return null;
    return root.querySelector('[data-sa-added-modal]');
  }

  function setCtaLoading(btn, on) {
    if (!btn) return;
    btn.classList.toggle('loading', on);
    if (on) {
      btn.setAttribute('aria-busy', 'true');
    } else {
      btn.removeAttribute('aria-busy');
    }
  }

  function setSaCardAddLoading(btn, on) {
    if (!btn) return;
    setCtaLoading(btn, on);
    if (on) {
      btn.dataset.saAddLocked = '1';
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      return;
    }
    if (btn.dataset.saAddLocked === '1') {
      btn.disabled = false;
      btn.removeAttribute('aria-disabled');
      delete btn.dataset.saAddLocked;
    }
  }

  // Keep the card claimed until the section-level confirmation modal is closed.
  // The Ajax request can finish before the shopper has interacted with the
  // modal; unlocking in fetch.finally() would allow a second click to submit the
  // same subscription and trigger Shopify's duplicate-subscription error.
  function lockCardAfterAdd(card, button) {
    if (!card) return;
    addedLockedCard = card;
    addedLockedButton = button || null;
    card.dataset.saAddModalLocked = '1';
    if (button) {
      setCtaLoading(button, false);
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
    }
  }

  function releaseCardAfterAdd() {
    if (addedLockedCard) delete addedLockedCard.dataset.saAddModalLocked;
    if (addedLockedButton) {
      delete addedLockedButton.dataset.saAddLocked;
      addedLockedButton.disabled = false;
      addedLockedButton.removeAttribute('aria-disabled');
      addedLockedButton.removeAttribute('aria-busy');
    }
    addedLockedCard = null;
    addedLockedButton = null;
  }

  function clearCardError(card) {
    if (!card) return;
    var existing = card.querySelector('.sa-collection-added__error');
    if (existing) existing.remove();
    card.classList.remove('has-sa-add-error');
  }

  function showCardError(card, message) {
    if (!card) return;
    clearCardError(card);
    var el = document.createElement('p');
    el.className = 'sa-collection-added__error';
    el.setAttribute('role', 'alert');
    el.textContent = message;
    card.appendChild(el);
    card.classList.add('has-sa-add-error');
    trackTimeout(function () {
      if (el.parentNode) el.remove();
      if (card.querySelector('.sa-collection-added__error') === null) {
        card.classList.remove('has-sa-add-error');
      }
    }, 10000);
  }

  function refreshCartCounts() {
    fetch('/cart.json')
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        if (cart.item_count === undefined) return;
        document.querySelectorAll('.cart-count').forEach(function (el) {
          if (el.classList.contains('cart-count-drawer')) {
            el.innerHTML = '(' + cart.item_count + ')';
          } else {
            el.innerHTML = cart.item_count > 100 ? '~' : cart.item_count;
          }
        });
        var totalPrice = document.querySelector('header-total-price');
        if (totalPrice && typeof totalPrice.updateTotal === 'function') {
          totalPrice.updateTotal(cart);
        }
        var freeShip = document.querySelector('free-ship-progress-bar');
        if (freeShip && typeof freeShip.init === 'function') {
          freeShip.init(cart.items_subtotal_price);
        }
      })
      .catch(function () {
        /* count refresh is best-effort; never block the confirmation UI */
      });
  }

  // Keep the (closed) minicart content in sync without opening it, mirroring the
  // theme's section-render replace. Best-effort and fully guarded.
  function syncMiniCartSections(cartEl, sections) {
    if (!cartEl || !sections || typeof cartEl.getSectionsToRender !== 'function') return;
    try {
      cartEl.getSectionsToRender().forEach(function (section) {
        var target = document.getElementById(section.id);
        if (!target || !sections[section.id]) return;
        var parsed = new DOMParser().parseFromString(sections[section.id], 'text/html');
        var minicart = parsed.querySelector('#minicart-form');
        if (minicart) target.innerHTML = minicart.innerHTML;
      });
      if (typeof cartEl.cartAction === 'function') cartEl.cartAction();
    } catch (e) {
      /* no-op: sync is non-critical */
    }
  }

  function ensureAddedProductSummary(modal) {
    var product = modal ? modal.querySelector('[data-sa-added-product]') : null;
    if (!product) {
      var template = modal ? modal.querySelector('[data-sa-added-product-template]') : null;
      if (template && template.content && template.content.firstElementChild) {
        product = template.content.firstElementChild.cloneNode(true);
        template.insertAdjacentElement('beforebegin', product);
      }
    }
    return {
      image: product ? product.querySelector('[data-sa-added-image]') : null,
      title: product ? product.querySelector('[data-sa-added-title]') : null,
      price: product ? product.querySelector('[data-sa-added-price]') : null,
    };
  }

  function openAddedModal(card, trigger) {
    var modal = getAddedModal(card);
    if (!modal || modal.classList.contains('is-open') || !modal.hidden) return;

    var img = card.querySelector('.product-item__media img, img.product-image');
    var titleEl = card.querySelector('.product-item__name');
    var priceEl = card.querySelector('.product-item__price');
    var title = titleEl ? titleEl.textContent.trim() : '';

    var productSummary = ensureAddedProductSummary(modal);
    var modalImg = productSummary.image;
    var modalTitle = productSummary.title;
    var modalPrice = productSummary.price;

    if (modalImg) {
      modalImg.src = img ? img.currentSrc || img.src : '';
      modalImg.alt = title;
    }
    if (modalTitle) modalTitle.textContent = title;
    if (modalPrice) {
      if (priceEl) {
        var clone = priceEl.cloneNode(true);
        clone
          .querySelectorAll('.sa-collection-product-card__price-soldout, .visually-hidden, .visually-hidden-price')
          .forEach(function (n) {
            n.remove();
          });
        modalPrice.textContent = clone.textContent.replace(/\s+/g, ' ').trim();
      } else {
        modalPrice.textContent = '';
      }
    }

    addedLastFocused = trigger || document.activeElement;
    lockCardAfterAdd(card, trigger);
    modal.hidden = false;
    // Force reflow so the transition runs when we add the open class.
    void modal.offsetWidth;
    modal.classList.add('is-open');
    document.body.classList.add('sa-collection-added-open');

    var dialog = modal.querySelector('[data-sa-added-dialog]');
    if (dialog && typeof trapFocus === 'function') {
      addedFocusCleanup = trapFocus(dialog);
    } else if (dialog) {
      dialog.focus();
    }
  }

  function closeAddedModal() {
    var modal = document.querySelector('[data-sa-added-modal]:not([hidden])');
    if (!modal) {
      releaseCardAfterAdd();
      return;
    }

    modal.classList.remove('is-open');
    document.body.classList.remove('sa-collection-added-open');
    // Release the card immediately when the shopper dismisses the modal. The
    // modal stays non-hidden during its exit transition, so the delegated add
    // handler still blocks clicks until the overlay has fully disappeared.
    releaseCardAfterAdd();

    if (addedFocusCleanup) {
      addedFocusCleanup();
      addedFocusCleanup = null;
    }

    var dialog = modal.querySelector('[data-sa-added-dialog]');
    var finalize = function () {
      modal.hidden = true;
    };
    if (dialog) {
      var done = false;
      var onEnd = function () {
        if (done) return;
        done = true;
        dialog.removeEventListener('transitionend', onEnd);
        finalize();
      };
      dialog.addEventListener('transitionend', onEnd);
      trackTimeout(onEnd, 280);
    } else {
      finalize();
    }

    if (addedLastFocused && typeof addedLastFocused.focus === 'function') {
      addedLastFocused.focus();
    }
    addedLastFocused = null;
  }

  // Check out → buy the selected product ONLY, bypassing the current cart.
  // Uses Shopify's cart permalink /cart/{variantId}:{qty}, which creates a
  // checkout containing just that line item (existing cart contents excluded).
  // The variant id is read live from the card's form so it follows swatch
  // (color) changes; falls back to the button's data-variant-id.
  function saCardCheckout(button, card) {
    if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;
    if (button.dataset.saCheckingOut === '1') return;

    var variantId = '';
    var sellingPlan = '';
    var form = card.querySelector('product-form form');
    if (form) {
      var idInput = form.querySelector('[name="id"]');
      if (idInput && idInput.value) variantId = idInput.value;
      var spInput = form.querySelector('[name="selling_plan"]');
      if (spInput && spInput.value) sellingPlan = spInput.value;
    }
    if (!variantId) variantId = button.getAttribute('data-variant-id') || '';
    if (!variantId) return;

    button.dataset.saCheckingOut = '1';
    setCtaLoading(button, true);

    var url = '/cart/' + encodeURIComponent(variantId) + ':1';
    if (sellingPlan) url += '?selling_plan=' + encodeURIComponent(sellingPlan);
    window.location.href = url;
  }

  function saCardAddToCart(form, submitButton, card) {
    var cartEl =
      document.querySelector('cart-notification') ||
      document.querySelector('cart-drawer');

    var config = {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/javascript',
      },
    };
    // The hidden variant id input ships with `disabled` and is normally enabled
    // by the global <product-form> upgrade; enable it here too so the add is
    // self-sufficient regardless of custom-element timing.
    var idInput = form.querySelector('[name="id"]');
    if (idInput) idInput.disabled = false;

    var formData = new FormData(form);
    if (cartEl && typeof cartEl.getSectionsToRender === 'function') {
      formData.append(
        'sections',
        cartEl.getSectionsToRender().map(function (s) {
          return s.id;
        })
      );
      formData.append('sections_url', window.location.pathname);
    }
    config.body = formData;

    var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
    var errorText =
      (getAddedModal(card) &&
        getAddedModal(card).getAttribute('data-error-text')) ||
      'Something went wrong. Please try again.';

    setSaCardAddLoading(submitButton, true);
    clearCardError(card);

    fetch(addUrl, config)
      .then(function (response) {
        return response.json();
      })
      .then(function (res) {
        if (res && (res.status || res.errors)) {
          var responseErrorText =
            res.description ||
            res.message ||
            (typeof res.errors === 'string'
              ? res.errors
              : res.errors
                ? JSON.stringify(res.errors)
                : errorText);
          showCardError(card, responseErrorText);
          return;
        }
        if (cartEl && res && res.sections) {
          syncMiniCartSections(cartEl, res.sections);
        }
        refreshCartCounts();
        openAddedModal(card, submitButton);
      })
      .catch(function () {
        showCardError(card, errorText);
      })
      .finally(function () {
        // A successful add is held until the confirmation modal closes. This
        // prevents a second click after the request resolves but before the
        // shopper dismisses the modal.
        if (card.dataset.saAddModalLocked !== '1') {
          setSaCardAddLoading(submitButton, false);
        }
        delete form.dataset.saAdding;
      });
  }

  function claimSaCardAdd(form, submitButton, card) {
    if (!form || !card || form.dataset.saAdding === '1') return false;
    if (card.dataset.saAddModalLocked === '1') return false;
    if (document.querySelector('[data-sa-added-modal]:not([hidden])')) return false;
    if (
      submitButton &&
      (submitButton.disabled || submitButton.getAttribute('aria-disabled') === 'true')
    ) {
      return false;
    }
    form.dataset.saAdding = '1';
    saCardAddToCart(form, submitButton, card);
    return true;
  }

  /* ---------- Delegated events (bound once, survive AJAX re-render) ---------- */

  function initDelegation() {
    if (delegationBound) return;
    delegationBound = true;

    const isMobile = () => window.matchMedia('(max-width: 767px)').matches;

    addTrackedListener(
      document,
      'click',
      (event) => {
        const trigger = event.target.closest('[data-sa-sort-trigger]');
        if (!trigger) return;
        const sortRoot = trigger.closest(SORT_SELECTOR);
        if (!sortRoot) return;

        event.preventDefault();
        event.stopPropagation();

        const willOpen = !sortRoot.classList.contains('is-open');
        closeAllSortDropdowns(sortRoot);
        sortRoot.classList.toggle('is-open', willOpen);
        trigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      },
      true
    );

    addTrackedListener(document, 'click', (event) => {
      // Added-to-cart modal: close via the X / Continue shopping buttons or the
      // overlay. View Cart is a plain link — let its default navigation happen.
      if (event.target.closest('[data-sa-added-close]')) {
        event.preventDefault();
        closeAddedModal();
        return;
      }
      if (event.target.closest('[data-sa-added-overlay]')) {
        closeAddedModal();
        return;
      }

      const disclosureTrigger = event.target.closest('[data-sa-text-disclosure-trigger]');
      if (disclosureTrigger) {
        const disclosure = disclosureTrigger.closest('[data-sa-text-disclosure]');
        if (!disclosure) return;

        event.preventDefault();
        event.stopPropagation();

        const willExpand = !disclosure.classList.contains('is-expanded');
        const content = disclosure.querySelector('[data-sa-text-disclosure-content]');
        if (!content) return;

        if (willExpand) {
          disclosure.classList.remove('is-collapsed');
          disclosure.classList.add('is-expanded');
          content.textContent = getTextDisclosureFullText(content);
          disclosureTrigger.setAttribute('aria-expanded', 'true');
          disclosureTrigger.setAttribute(
            'aria-label',
            disclosureTrigger.getAttribute('data-collapse-label') || ''
          );
        } else {
          disclosure.classList.remove('is-expanded');
          measureTextDisclosure(disclosure);
        }
        updateTextDisclosureContainer(disclosure);
        return;
      }

      // Check out button on a v2 card → buy just this product (cart permalink).
      if (normalizeProductDetailNavigation(event)) {
        return;
      }

      const emptyReset = event.target.closest('[data-sa-empty-reset]');
      if (emptyReset) {
        event.preventDefault();
        resetClientCollectionFilters(emptyReset.getAttribute('href'));
        return;
      }

      const checkoutBtn = event.target.closest('[data-sa-checkout]');
      if (checkoutBtn) {
        const checkoutCard = checkoutBtn.closest('.sa-collection-product-card');
        if (checkoutCard && checkoutBtn.closest(ROOT_SELECTOR)) {
          event.preventDefault();
          saCardCheckout(checkoutBtn, checkoutCard);
          return;
        }
      }

      // Sort options: build the target from the CURRENT url (window.location) so
      // view/preview/theme-editor/section params and every active filter are
      // preserved — only sort_by changes. This keeps the fetch on
      // collection.v2-refactor instead of the a.href fallback (collection.url),
      // which would drop ?view= and return the default collection template.
      const sortOption = event.target.closest(SORT_OPTION_SELECTOR);
      if (sortOption) {
        event.preventDefault();
        const value =
          sortOption.getAttribute('data-sort-value') || sortOption.getAttribute('value');
        closeAllSortDropdowns(null);
        document.querySelectorAll('[data-sa-sort-sheet].is-open').forEach(closeSortSheet);
        let target;
        if (value) {
          try {
            const url = new URL(window.location.href);
            url.searchParams.set('sort_by', value);
            target = url.toString();
          } catch (e) {
            target = sortOption.getAttribute('href');
          }
        } else {
          target = sortOption.getAttribute('href');
        }
        if (target) navigateFacet(target);
        return;
      }

      // Client-side pill tabs (S&D not configured): filter loaded cards in place.
      const csTab = event.target.closest('[data-sa-cs-tab]');
      if (csTab) {
        event.preventDefault();
        if (csTab.classList.contains('is-active')) return;
        navigateClientTabFilter(csTab.getAttribute('data-cs-key') || '');
        return;
      }

      // Big image category nav in navigate mode: keep the v2 page shell and
      // refresh the target collection in place. Non-collection/external links
      // fall back to normal navigation inside navigateCategory().
      const categoryLink = event.target.closest('[data-sa-category-navigate]');
      if (categoryLink) {
        const href = categoryLink.getAttribute('href');
        if (href) {
          event.preventDefault();
          navigateCategory(href);
        }
        return;
      }

      // Big image category nav: filter current page by collection membership
      // (a separate dimension from the pill tabs). href is the no-JS fallback.
      const seriesTab = event.target.closest('[data-sa-series-filter]');
      if (seriesTab) {
        event.preventDefault();
        let nextSeriesKey = seriesTab.getAttribute('data-series-key') || '';
        if (!nextSeriesKey) {
          const href = seriesTab.getAttribute('href') || '';
          const match = href.match(/\/collections\/([^/?#]+)/);
          nextSeriesKey = match && match[1] ? match[1] : '';
        }
        applyClientSeriesFilter(nextSeriesKey);
        return;
      }

      // Color swatch inside a v2 card: don't preventDefault (let the theme's
      // variant-radios swap image/price); just reflect the selected color's
      // availability onto the card's CTA + price.
      const cardSwatch = event.target.closest(
        '.sa-collection-product-card .product__color-swatches--js'
      );
      if (cardSwatch) {
        applyCardColorAvailability(
          cardSwatch.closest('.sa-collection-product-card'),
          cardSwatch
        );
      }

      // Facet tabs are Shopify Search & Discovery links (url_to_remove / append):
      // those already carry the current query context (view, other filters), so
      // the href is trusted. JS just upgrades them to a scoped AJAX refresh.
      const facetTab = event.target.closest(FACET_TAB_SELECTOR);
      if (facetTab) {
        const href = facetTab.getAttribute('href');
        if (href) {
          event.preventDefault();
          navigateFacet(href);
        }
        return;
      }

      const overlay = event.target.closest('[data-sa-sort-sheet-overlay]');
      if (overlay) {
        closeSortSheet(overlay.closest('[data-sa-sort-sheet]'));
        return;
      }

      if (!event.target.closest(SORT_SELECTOR)) {
        closeAllSortDropdowns(null);
      }
    });

    addTrackedListener(document, 'keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (document.querySelector('[data-sa-added-modal]:not([hidden])')) {
        closeAddedModal();
        return;
      }
      closeAllSortDropdowns(null);
      document.querySelectorAll('[data-sa-sort-sheet].is-open').forEach(closeSortSheet);
    });

    addTrackedListener(window, 'popstate', () => {
      closeCollectionQuickview();
      navigateCategory(window.location.href, false);
    });

    // Claim direct V2 card clicks before a theme-level product-form click
    // handler can submit the same subscription twice. The form lock also covers
    // rapid taps that occur before the confirmation modal is visible.
    addTrackedListener(
      document,
      'click',
      (event) => {
        const submitButton = event.target.closest(
          '.sa-collection-product-card product-form form [type="submit"][name="add"]'
        );
        if (!submitButton || !submitButton.closest(ROOT_SELECTOR)) return;
        const form = submitButton.form || submitButton.closest('form');
        const card = submitButton.closest('.sa-collection-product-card');
        if (!form || !card || form.getAttribute('data-type') !== 'add-to-cart-form') return;

        event.preventDefault();
        event.stopImmediatePropagation();
        claimSaCardAdd(form, submitButton, card);
      },
      true
    );

    // Intercept the collection card's direct add-to-cart submit in the CAPTURE
    // phase, before the global <product-form> handler (bound on the form in the
    // target phase) can open the site minicart. Only the v2 collection's direct
    // add form is claimed; Select Options / quickview and all other forms flow
    // through untouched.
    addTrackedListener(
      document,
      'submit',
      (event) => {
        const form = event.target;
        if (!form || form.tagName !== 'FORM') return;
        if (form.getAttribute('data-type') !== 'add-to-cart-form') return;
        const card = form.closest('.sa-collection-product-card');
        if (!card || !form.closest(ROOT_SELECTOR)) return;
        if (!form.closest('product-form')) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const submitButton = form.querySelector('[type="submit"]');
        claimSaCardAdd(form, submitButton, card);
      },
      true
    );
  }

  /* ---------- Big image nav: horizontal swipe progress indicator + edge fade ----------
     The category nav row (a separate section) is horizontally scrollable. This
     mirrors scroll position into a progress bar (thumb width = viewport/content,
     offset = scroll progress) and toggles the right-edge fade. Bound once; the nav
     lives outside #productgridcontainer so it survives facet/sort AJAX swaps. */
  function initCategoryNavIndicator() {
    const nav = document.querySelector(CATEGORY_NAV_SELECTOR);
    if (!nav) return;
    const track = nav.querySelector('.sa-collection-main__category-nav-track');
    if (!track || track.dataset.saIndicatorBound) return;
    track.dataset.saIndicatorBound = '1';

    const scrollStep = () => {
      const item = track.querySelector('.sa-collection-main__category-item');
      const gap = parseInt(window.getComputedStyle(track).gap, 10) || 16;
      return item ? item.offsetWidth + gap : Math.round(track.clientWidth * 0.75);
    };

    const update = () => {
      const scrollW = track.scrollWidth;
      const clientW = track.clientWidth;
      const maxScroll = scrollW - clientW;
      const scrollable = maxScroll > 1;
      const hasMultipleCategories =
        track.querySelectorAll('.sa-collection-main__category-item').length > 2;
      nav.classList.toggle('is-scrollable', scrollable);
      nav.classList.toggle('has-category-indicator', hasMultipleCategories);
      const progress = maxScroll > 0 ? track.scrollLeft / maxScroll : 0;
      const progressPct = scrollable
        ? Math.min(
            100,
            Math.max((clientW / scrollW) * 100, ((track.scrollLeft + clientW) / scrollW) * 100)
          )
        : 100;
      nav.style.setProperty('--sa-nav-progress', progressPct + '%');
      const atStart = !scrollable || track.scrollLeft <= 1;
      const atEnd = !scrollable || progress >= 0.99;
      nav.classList.toggle('is-scroll-end', atEnd);
      nav.classList.toggle('is-nav-start', atStart);
      nav.classList.toggle('is-nav-end', atEnd);
    };

    const scrollActiveCategoryIntoView = () => {
      const active = track.querySelector('.sa-collection-main__category-item.is-active');
      const maxScroll = track.scrollWidth - track.clientWidth;
      if (!active || maxScroll <= 1) return;
      const activeRect = active.getBoundingClientRect();
      const trackRect = track.getBoundingClientRect();
      const activeCenterInTrack =
        activeRect.left - trackRect.left + track.scrollLeft + activeRect.width / 2;
      const targetLeft = Math.max(
        0,
        Math.min(maxScroll, activeCenterInTrack - track.clientWidth / 2)
      );
      track.classList.add('is-programmatic-scroll');
      track.scrollTo({ left: targetLeft, behavior: 'smooth' });
      update();
      trackTimeout(() => {
        track.classList.remove('is-programmatic-scroll');
        update();
      }, 350);
    };

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        update();
        ticking = false;
      });
    };
    addTrackedListener(track, 'scroll', onScroll, { passive: true });
    addTrackedListener(window, 'resize', onScroll, { passive: true });

    const prevBtn = nav.querySelector('[data-sa-category-nav-prev]');
    const nextBtn = nav.querySelector('[data-sa-category-nav-next]');
    if (prevBtn && !prevBtn.dataset.saNavBound) {
      prevBtn.dataset.saNavBound = '1';
      addTrackedListener(prevBtn, 'click', () => {
        track.scrollBy({ left: -scrollStep(), behavior: 'smooth' });
      });
    }
    if (nextBtn && !nextBtn.dataset.saNavBound) {
      nextBtn.dataset.saNavBound = '1';
      addTrackedListener(nextBtn, 'click', () => {
        track.scrollBy({ left: scrollStep(), behavior: 'smooth' });
      });
    }

    initCategoryNavDrag(track, update);
    update();
    window.requestAnimationFrame(scrollActiveCategoryIntoView);
  }

  function initCategoryNavDrag(track, update) {
    if (!track || track.dataset.saDragBound) return;
    track.dataset.saDragBound = '1';

    let pressed = false;
    let dragging = false;
    let moved = false;
    let startX = 0;
    let startY = 0;
    let startScroll = 0;
    let suppressClickUntil = 0;

    const canDrag = () => track.scrollWidth > track.clientWidth + 1;
    const getEventX = (event) => event.pageX || event.clientX || 0;
    const getEventY = (event) => event.pageY || event.clientY || 0;

    const beginDrag = (event) => {
      if (event.button !== undefined && event.button !== 0) return;
      if (!canDrag()) return;
      pressed = true;
      dragging = false;
      moved = false;
      startX = getEventX(event);
      startY = getEventY(event);
      startScroll = track.scrollLeft;
    };

    const drag = (event) => {
      if (!pressed) return;
      const deltaX = getEventX(event) - startX;
      const deltaY = getEventY(event) - startY;

      if (!dragging) {
        if (Math.abs(deltaX) <= Math.abs(deltaY) || Math.abs(deltaX) <= 6) return;
        dragging = true;
        moved = true;
        track.classList.add('is-dragging');
        document.body.classList.add('sa-category-nav-dragging');
      }

      track.scrollLeft = startScroll - deltaX;
      update();
    };

    const endDrag = () => {
      if (!pressed && !dragging) return;
      pressed = false;
      const didMove = moved;
      dragging = false;
      if (didMove) suppressClickUntil = Date.now() + 350;
      trackTimeout(() => {
        moved = false;
      }, 350);
      track.classList.remove('is-dragging');
      document.body.classList.remove('sa-category-nav-dragging');
    };

    addTrackedListener(track, 'mousedown', beginDrag);
    addTrackedListener(track, 'dragstart', (event) => event.preventDefault());
    addTrackedListener(document, 'mousemove', drag);
    addTrackedListener(document, 'mouseup', endDrag);
    addTrackedListener(document, 'mouseleave', endDrag);

    addTrackedListener(
      track,
      'touchstart',
      (event) => {
        if (!canDrag() || !event.touches.length) return;
        pressed = true;
        dragging = false;
        moved = false;
        startX = event.touches[0].pageX;
        startY = event.touches[0].pageY;
        startScroll = track.scrollLeft;
      },
      { passive: true }
    );

    addTrackedListener(
      track,
      'touchmove',
      (event) => {
        if (!pressed || !event.touches.length) return;
        const deltaX = event.touches[0].pageX - startX;
        const deltaY = event.touches[0].pageY - startY;

        if (!dragging) {
          if (Math.abs(deltaX) <= Math.abs(deltaY) || Math.abs(deltaX) <= 6) return;
          dragging = true;
          moved = true;
          track.classList.add('is-dragging');
          document.body.classList.add('sa-category-nav-dragging');
        }

        track.scrollLeft = startScroll - deltaX;
        update();
      },
      { passive: true }
    );

    addTrackedListener(track, 'touchend', endDrag);
    addTrackedListener(track, 'touchcancel', endDrag);

    addTrackedListener(
      track,
      'click',
      (event) => {
        if (!moved && Date.now() > suppressClickUntil) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
  }

  function initPromoRailIndicator() {
    const rail = document.querySelector(PROMO_RAIL_SELECTOR);
    if (!rail) return;
    const track = rail.querySelector('[data-sa-promo-rail-track]');
    if (!track || track.dataset.saIndicatorBound) return;
    track.dataset.saIndicatorBound = '1';

    const scrollStep = () => {
      const item = track.querySelector('.sa-collection-promotion-card');
      const gap = parseInt(window.getComputedStyle(track).gap, 10) || 8;
      return item ? item.offsetWidth + gap : Math.round(track.clientWidth * 0.85);
    };

    const update = () => {
      const scrollW = track.scrollWidth;
      const clientW = track.clientWidth;
      const maxScroll = scrollW - clientW;
      const scrollable = maxScroll > 1;
      rail.classList.toggle('is-scrollable', scrollable);
      const progress = maxScroll > 0 ? track.scrollLeft / maxScroll : 0;
      const progressPct = scrollable
        ? Math.min(
            100,
            Math.max((clientW / scrollW) * 100, ((track.scrollLeft + clientW) / scrollW) * 100)
          )
        : 100;
      rail.style.setProperty('--sa-promo-rail-progress', progressPct + '%');
      const atStart = !scrollable || track.scrollLeft <= 1;
      const atEnd = !scrollable || progress >= 0.99;
      rail.classList.toggle('is-scroll-end', atEnd);
      rail.classList.toggle('is-nav-start', atStart);
      rail.classList.toggle('is-nav-end', atEnd);
    };

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        update();
        ticking = false;
      });
    };
    addTrackedListener(track, 'scroll', onScroll, { passive: true });
    addTrackedListener(window, 'resize', onScroll, { passive: true });

    const prevBtn = rail.querySelector('[data-sa-promo-rail-prev]');
    const nextBtn = rail.querySelector('[data-sa-promo-rail-next]');
    if (prevBtn && !prevBtn.dataset.saRailBound) {
      prevBtn.dataset.saRailBound = '1';
      addTrackedListener(prevBtn, 'click', () => {
        track.scrollBy({ left: -scrollStep(), behavior: 'smooth' });
      });
    }
    if (nextBtn && !nextBtn.dataset.saRailBound) {
      nextBtn.dataset.saRailBound = '1';
      addTrackedListener(nextBtn, 'click', () => {
        track.scrollBy({ left: scrollStep(), behavior: 'smooth' });
      });
    }

    initCategoryNavDrag(track, update);
    update();
  }

  /* ---------- Pill tabs: right-edge fade (half-reveal overflowing last tab) ----------
     The tabs live inside #productgridcontainer and are re-rendered by facet/sort
     AJAX, so scroll listeners are (re)bound per tabs element (dataset-guarded) and
     the resize listener is bound once. The fade hides at the scroll end so the
     final tab stays fully readable. */
  function scrollFilterTabIntoView(tab) {
    const scroller = tab && tab.closest('[data-sa-filter-tabs]');
    if (!scroller) return;
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    if (maxScroll <= 1) return;
    const tabRect = tab.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    const tabCenterInScroller =
      tabRect.left - scrollerRect.left + scroller.scrollLeft + tabRect.width / 2;
    const targetLeft = Math.max(
      0,
      Math.min(maxScroll, tabCenterInScroller - scroller.clientWidth / 2)
    );
    scroller.classList.add('is-programmatic-scroll');
    scroller.scrollTo({ left: targetLeft, behavior: 'smooth' });
    window.requestAnimationFrame(updateFilterTabsFade);
    trackTimeout(() => {
      scroller.classList.remove('is-programmatic-scroll');
      updateFilterTabsFade();
    }, 350);
  }

  function updateFilterTabsFade() {
    const tabs = document.querySelector('[data-sa-filter-tabs]');
    if (!tabs) return;
    const wrap = tabs.closest('.sa-collection-main__filter-tabs-wrap');
    if (!wrap) return;
    const maxScroll = tabs.scrollWidth - tabs.clientWidth;
    const scrollable = maxScroll > 1;
    wrap.classList.toggle('is-tabs-scrollable', scrollable);
    wrap.classList.toggle('is-tabs-end', scrollable && tabs.scrollLeft >= maxScroll - 1);
    updateActiveTabDividerLine(tabs);
  }

  function updateActiveTabDividerLine(tabs) {
    const scroller = tabs || document.querySelector('[data-sa-filter-tabs]');
    const controlBar = scroller && scroller.closest('.sa-collection-main__control-bar');
    const divider =
      controlBar && controlBar.querySelector('.sa-collection-main__toolbar-divider');
    if (!scroller || !divider) return;

    const active = scroller.querySelector('.sa-collection-main__filter-tab.is-active');
    if (!active) {
      divider.style.setProperty('--sa-active-tab-line-opacity', '0');
      divider.style.setProperty('--sa-active-tab-line-width', '0px');
      return;
    }

    const activeRect = active.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    const dividerRect = divider.getBoundingClientRect();
    const visibleLeft = Math.max(activeRect.left, scrollerRect.left);
    const visibleRight = Math.min(activeRect.right, scrollerRect.right);
    if (visibleRight <= visibleLeft) {
      divider.style.setProperty('--sa-active-tab-line-opacity', '0');
      divider.style.setProperty('--sa-active-tab-line-width', '0px');
      return;
    }

    const lineLeft = Math.max(0, Math.round(visibleLeft - dividerRect.left));
    const lineRight = Math.min(
      Math.round(dividerRect.width),
      Math.round(visibleRight - dividerRect.left)
    );
    divider.style.setProperty('--sa-active-tab-line-left', lineLeft + 'px');
    divider.style.setProperty('--sa-active-tab-line-width', Math.max(0, lineRight - lineLeft) + 'px');
    divider.style.setProperty('--sa-active-tab-line-opacity', '1');
  }

  function initFilterTabsFade() {
    const tabs = document.querySelector('[data-sa-filter-tabs]');
    if (!tabs) return;
    if (!tabs.dataset.saFadeBound) {
      tabs.dataset.saFadeBound = '1';
      let ticking = false;
      addTrackedListener(
        tabs,
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(() => {
            updateFilterTabsFade();
            ticking = false;
          });
        },
        { passive: true }
      );
    }
    initFilterTabsDrag(tabs);
    if (!filterTabsResizeBound) {
      filterTabsResizeBound = true;
      addTrackedListener(
        window,
        'resize',
        () => window.requestAnimationFrame(updateFilterTabsFade),
        { passive: true }
      );
    }
    updateFilterTabsFade();
  }

  function initFilterTabsDrag(tabs) {
    if (!tabs || tabs.dataset.saDragBound) return;
    tabs.dataset.saDragBound = '1';

    let pressed = false;
    let dragging = false;
    let moved = false;
    let startX = 0;
    let startScroll = 0;

    const getEventX = (event) => event.pageX || event.clientX || 0;

    const startDrag = (event) => {
      if (event.button !== 0) return;
      if (tabs.scrollWidth <= tabs.clientWidth + 1) return;
      pressed = true;
      dragging = false;
      moved = false;
      startX = getEventX(event);
      startScroll = tabs.scrollLeft;
    };

    const drag = (event) => {
      if (!pressed) return;
      const delta = getEventX(event) - startX;
      if (!dragging) {
        if (Math.abs(delta) <= 6) return;
        dragging = true;
        moved = true;
        tabs.classList.add('is-dragging');
        document.body.classList.add('sa-filter-tabs-dragging');
      }
      tabs.scrollLeft = startScroll - delta;
      updateFilterTabsFade();
    };

    const endDrag = () => {
      if (!pressed && !dragging) return;
      pressed = false;
      dragging = false;
      trackTimeout(() => {
        moved = false;
      }, 0);
      tabs.classList.remove('is-dragging');
      document.body.classList.remove('sa-filter-tabs-dragging');
    };

    addTrackedListener(tabs, 'mousedown', startDrag);
    addTrackedListener(document, 'mousemove', drag);
    addTrackedListener(document, 'mouseup', endDrag);
    addTrackedListener(document, 'mouseleave', endDrag);

    addTrackedListener(
      tabs,
      'touchstart',
      (event) => {
        if (tabs.scrollWidth <= tabs.clientWidth + 1) return;
        pressed = true;
        dragging = false;
        moved = false;
        startX = event.touches[0].pageX;
        startScroll = tabs.scrollLeft;
      },
      { passive: true }
    );

    addTrackedListener(
      tabs,
      'touchmove',
      (event) => {
        if (!pressed) return;
        const delta = event.touches[0].pageX - startX;
        if (!dragging) {
          if (Math.abs(delta) <= 6) return;
          dragging = true;
          moved = true;
          tabs.classList.add('is-dragging');
          document.body.classList.add('sa-filter-tabs-dragging');
        }
        if (event.cancelable) event.preventDefault();
        tabs.scrollLeft = startScroll - delta;
        updateFilterTabsFade();
      },
      { passive: false }
    );

    addTrackedListener(tabs, 'touchend', endDrag);
    addTrackedListener(tabs, 'touchcancel', endDrag);

    addTrackedListener(
      tabs,
      'click',
      (event) => {
        if (!moved) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
  }

  function scheduleDynamicTabsRender() {
    renderDynamicTabs();
    window.requestAnimationFrame(() => {
      renderDynamicTabs();
      updateFilterTabsFade();
    });
    trackTimeout(() => {
      renderDynamicTabs();
      updateFilterTabsFade();
    }, 200);
    trackTimeout(() => {
      renderDynamicTabs();
      updateFilterTabsFade();
    }, 800);
  }

  function init() {
    const root = document.querySelector(ROOT_SELECTOR);
    const grid = document.querySelector(GRID_SELECTOR);
    if (!root || !grid) return;

    initSticky();
    initDelegation();
    initTextDisclosures();
    if (root === initializedRoot && grid === initializedGrid) {
      return;
    }

    initializedRoot = root;
    initializedGrid = grid;
    initInfinite();
    initPromoRepairObserver();
    initPromoRootRepairObserver();
    initCategoryNavIndicator();
    initPromoRailIndicator();
    initCtaLabelObserver();
    normalizeCollectionCountLabels();
    repairCollectionCtaLabels();
    scheduleDynamicTabsRender();
    schedulePromoRepair();
    setActiveSeriesTab(activeSeriesKey);
    updateBreadcrumbTitleFromActiveCategoryNav();
    revealCardImages();
    initCardColorStates();
    trackTimeout(repairCollectionCtaLabels, 250);
    trackTimeout(repairCollectionCtaLabels, 800);
    trackTimeout(repairCollectionCtaLabels, 1600);
  }

  if (document.readyState === 'loading') {
    addTrackedListener(document, 'DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:unload', handleSectionUnload);
  document.addEventListener('shopify:section:load', handleSectionLoad);
  addTrackedListener(window, 'glozinspeed-DOMContentLoaded', init);
  addTrackedListener(document, 'glozinspeed-DOMContentLoaded', init);
  addTrackedListener(window, 'load', schedulePromoRepair);
  addTrackedListener(window, 'load', scheduleCtaLabelRepair);
  addTrackedListener(window, 'pageshow', schedulePromoRepair);
  addTrackedListener(window, 'pageshow', scheduleCtaLabelRepair);
  addTrackedListener(window, 'pageshow', scheduleHistoryRestore);
  addTrackedListener(window, 'glozinspeed-load', schedulePromoRepair);
  addTrackedListener(window, 'glozinspeed-load', scheduleCtaLabelRepair);
  addTrackedListener(window, 'glozinspeed-pageshow', schedulePromoRepair);
  addTrackedListener(window, 'glozinspeed-pageshow', scheduleCtaLabelRepair);
  window[INSTANCE_DESTROY_KEY] = destroyCollectionMain;
})();
